import { AppSelect } from '../../ui/AppSelect';
import { ModelArkReferenceLibraryPanel } from '../../assets/ModelArkLibraryControls';
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle, Check, CheckCircle2, ChevronDown, ChevronUp, FileText, Film,
  Images, Lightbulb, Loader2, MessageSquareText, PackagePlus, RefreshCw, RemoveFormatting,
  CircleStop, Copy, Edit3, History, Send, Settings2, Volume2, X, ZoomIn,
  Download,
} from "lucide-react";
import { LIGHTING_PRESETS } from "../../storyboard/common/LightingPresets";
import { createMentionChipSmall, renderPromptToEditor, serializeEditor } from "../../../utils/mentionUtils";
import AutoGrowTextarea from "./AutoGrowTextarea";
import {
  SURFACE, REF_LIMIT, BOARD_SECONDS, SHOW_LEGACY_STORYBOARD_LAYOUT,
  GUA_VIDEO_RESOLUTION, GUA_VIDEO_RATIOS,
  DEFAULT_VIDEO_LIGHTING, DEFAULT_VIDEO_RATIO,
} from "./constants";
import {
  firstText, normalizeImageUrl, normalizeVideoDuration,
  buildDefaultVideoPackageItems, buildAssetVideoPackageItems,
  enrichVideoPackageItems, normalizeVideoPackageItems,
  buildBoardVideoPrompt, buildPreviousBoardSummary,
  ensureVideoDraft, hydratePromptReferenceChips, getAssetMetaForUrl, getPackageItemGuaMeta,
} from "./utils";
import HoverImagePreview from "./HoverImagePreview";
import SelectedRefsPanel from "./SelectedRefsPanel";
import { assetLibraryLabel, getEffectiveAssetMeta } from "../../../utils/guaVideoAssetStatus";
import {
  getVideoResolutionsForProvider,
  normalizeVideoResolutionForProvider,
} from "../../../utils/projectRuntime";
import {
  getSeedance25Capabilities,
  getSeedance25ReferenceMediaType,
  normalizeSeedance25Duration,
  normalizeSeedance25Ratio,
  normalizeSeedance25Resolution,
} from "../../../utils/seedance25Capabilities";

const ReferenceMediaThumbnail = ({ item }) => {
  const type = getSeedance25ReferenceMediaType(item);
  if (type === "video") return <video src={item.url} muted preload="metadata" className="w-full h-full object-cover" />;
  if (type === "audio") return <span className="flex h-full items-center justify-center gap-2 text-xs"><Volume2 size={18} />音频参考</span>;
  return <img src={item.url} alt={item.name} className="w-full h-full object-cover" />;
};

const FOCUS_DETAIL_TABS = [
  { id: "boardPrompt", label: "本页提示词" },
  { id: "videoPrompt", label: "视频提示词" },
  { id: "references", label: "视频输入包" },
  { id: "globalRefs", label: "全局参考图" },
  { id: "directorChat", label: "跟导演对话" },
];

const FOCUS_COLUMN_DEFAULTS = [55, 20, 25];
const FOCUS_COLUMN_MIN_WIDTHS = [520, 240, 320];

const normalizeFocusColumnPercents = (value) => {
  if (!Array.isArray(value) || value.length !== 3) return FOCUS_COLUMN_DEFAULTS;
  const numbers = value.map((item) => Number(item));
  if (numbers.some((item) => !Number.isFinite(item) || item <= 0)) return FOCUS_COLUMN_DEFAULTS;
  const total = numbers.reduce((sum, item) => sum + item, 0);
  if (!Number.isFinite(total) || total <= 0) return FOCUS_COLUMN_DEFAULTS;
  return numbers.map((item) => Math.round((item / total) * 10000) / 100);
};

const StoryboardBoardListItemBase = ({
  projectId,
  board,
  boards,
  theme,
  episode,
  selectedRefs,
  aspectRatio,
  directorRequest,
  setDirectorRequest,
  onSendDirectorMessage,
  onOpenReferencePicker,
  onOpenVideoReferencePicker,
  onRemoveSelectedRef,
  onMatchRefsFromScript,
  onClearSelectedRefs,
  pendingDirectorAction,
  onExecutePendingSingleBoard,
  onExecutePendingAllBoards,
  onKeepPendingAsNote,
  onCancelPendingAction,
  directorBusy,
  loading,
  hasGeneratedBoards,
  presentationMode = "formal",
  onPreview,
  boardImageHistory,
  onSelectBoardImageHistory,
  onRegenerate,
  regenerating,
  disabled,
  onGenerateVideo,
  onStopVideo,
  videoDraft,
  onVideoDraftChange,
  videoOptionsOpen,
  onToggleVideoOptions,
  onSelectVideoHistory,
  onDownloadVideoHistory,
  videoTask,
  videoGenerating,
  onEraseSubtitle,
  assetMetaMap,
  onUploadPackageItem,
  onUploadBoardAsset,
  uploadingAssetUrls,
  videoProvider = "guavideo",
  videoProviderLabel = "GuaVideo",
  showGuaAssetUpload = true,
  focusMode = false,
  focusColumnPercents: controlledFocusColumnPercents,
  onFocusColumnPercentsChange,
}) => {
  const seedance25Capabilities = getSeedance25Capabilities(videoProvider);
  const isSeedance25 = Boolean(seedance25Capabilities);
  const videoResolutionOptions = useMemo(
    () => seedance25Capabilities?.resolutions || getVideoResolutionsForProvider(videoProvider),
    [seedance25Capabilities, videoProvider],
  );
  const videoReferenceLimit = seedance25Capabilities?.maxReferenceImages || 9;
  const hasAudioControl = !isSeedance25 || seedance25Capabilities.supportsGenerateAudioControl;
  const status = board.status || (board.imageUrl ? "success" : "pending");
  const isFailed = status === "failed";
  const isSuccess = status === "success";
  const boardImageUrl = normalizeImageUrl(board.imageUrl);
  const boardAssetMeta = getAssetMetaForUrl(assetMetaMap, boardImageUrl);
  const boardEffectiveAssetMeta = getEffectiveAssetMeta(boardAssetMeta, videoProvider);
  const boardAssetUploading = boardImageUrl ? uploadingAssetUrls?.has(boardImageUrl) : false;
  const boardAssetProcessing = !!boardEffectiveAssetMeta.assetId && boardEffectiveAssetMeta.status === "Processing";
  const boardAssetActive = !!boardEffectiveAssetMeta.assetId && boardEffectiveAssetMeta.status === "Active";
  const videoStatus = videoTask?.status || "";
  const videoUrl = firstText(videoTask?.videoUrl, videoTask?.url);
  const videoStopped = ["stopped", "cancelled", "canceled"].includes(videoStatus);
  const videoFailed = videoStatus === "failed";
  const videoSuccess = videoStatus === "success" && videoUrl;
  const videoBusy = videoGenerating || ["submitting", "processing", "queued", "polling"].includes(videoStatus);
  const subtitleBusy = videoTask?.postProcessStatus === "erasing_subtitle";
  const shouldHydrateVideoConfig = focusMode || videoOptionsOpen || videoBusy || videoSuccess || videoFailed;
  const defaultPackageItems = useMemo(
    () => buildDefaultVideoPackageItems(board, boards, selectedRefs, videoReferenceLimit),
    [board, boards, selectedRefs, videoReferenceLimit]
  );
  const assetCandidateItems = useMemo(
    () => (shouldHydrateVideoConfig ? buildAssetVideoPackageItems(selectedRefs, videoReferenceLimit) : []),
    [selectedRefs, shouldHydrateVideoConfig, videoReferenceLimit]
  );
  const enrichedAssetCandidateItems = useMemo(
    () => (shouldHydrateVideoConfig ? enrichVideoPackageItems(assetCandidateItems, [], assetMetaMap, videoReferenceLimit) : []),
    [assetCandidateItems, assetMetaMap, shouldHydrateVideoConfig, videoReferenceLimit]
  );
  const candidatePackageItems = useMemo(
    () => (shouldHydrateVideoConfig ? normalizeVideoPackageItems([...defaultPackageItems, ...enrichedAssetCandidateItems], videoReferenceLimit) : defaultPackageItems),
    [enrichedAssetCandidateItems, defaultPackageItems, shouldHydrateVideoConfig, videoReferenceLimit]
  );
  const draftState = useMemo(
    () => {
      if (shouldHydrateVideoConfig) {
        const ensured = ensureVideoDraft(board, boards, selectedRefs, videoDraft, aspectRatio, assetMetaMap, videoReferenceLimit);
        return isSeedance25 ? {
          ...ensured,
          duration: normalizeSeedance25Duration(videoProvider, videoDraft?.duration ?? board.durationSeconds ?? BOARD_SECONDS),
          ratio: normalizeSeedance25Ratio(videoProvider, ensured.ratio, aspectRatio),
          resolution: normalizeSeedance25Resolution(videoProvider, ensured.resolution),
        } : {
          ...ensured,
          resolution: normalizeVideoResolutionForProvider(
            videoProvider,
            ensured.resolution,
            GUA_VIDEO_RESOLUTION,
          ),
        };
      }
      const rawDraft = videoDraft && typeof videoDraft === "object" ? videoDraft : {};
      const inputPackage = Array.isArray(rawDraft.inputPackage)
        ? normalizeVideoPackageItems(rawDraft.inputPackage, videoReferenceLimit)
        : defaultPackageItems;
      return {
        ...rawDraft,
        duration: isSeedance25
          ? normalizeSeedance25Duration(videoProvider, rawDraft.duration ?? board.durationSeconds ?? BOARD_SECONDS)
          : normalizeVideoDuration(rawDraft.duration ?? board.durationSeconds ?? BOARD_SECONDS),
        ratio: isSeedance25
          ? normalizeSeedance25Ratio(videoProvider, rawDraft.ratio, aspectRatio)
          : firstText(rawDraft.ratio, aspectRatio, DEFAULT_VIDEO_RATIO),
        resolution: isSeedance25
          ? normalizeSeedance25Resolution(videoProvider, rawDraft.resolution)
          : normalizeVideoResolutionForProvider(
            videoProvider,
            rawDraft.resolution,
            GUA_VIDEO_RESOLUTION,
          ),
        generateAudio: rawDraft.generateAudio ?? true,
        noSubtitle: rawDraft.noSubtitle ?? true,
        inputPackage,
        lighting: firstText(rawDraft.lighting, DEFAULT_VIDEO_LIGHTING),
        previousSummary: rawDraft.previousSummary || "",
      };
    },
    [assetMetaMap, aspectRatio, board, boards, defaultPackageItems, isSeedance25, seedance25Capabilities, selectedRefs, shouldHydrateVideoConfig, videoDraft, videoProvider, videoReferenceLimit]
  );
  const videoRefs = draftState.inputPackage;
  const duration = isSeedance25
    ? normalizeSeedance25Duration(videoProvider, draftState.duration ?? board.durationSeconds ?? BOARD_SECONDS)
    : normalizeVideoDuration(draftState.duration ?? board.durationSeconds ?? BOARD_SECONDS);
  const ratio = firstText(draftState.ratio, aspectRatio, DEFAULT_VIDEO_RATIO);
  const resolution = isSeedance25
    ? normalizeSeedance25Resolution(videoProvider, draftState.resolution)
    : normalizeVideoResolutionForProvider(
      videoProvider,
      draftState.resolution,
      GUA_VIDEO_RESOLUTION,
    );
  const generateAudio = draftState.generateAudio ?? true;
  const noSubtitle = draftState.noSubtitle ?? true;
  const selectedLighting = firstText(draftState.lighting, DEFAULT_VIDEO_LIGHTING);
  const previousSummary = draftState.previousSummary || "";
  const defaultVideoPrompt = useMemo(
    () => (focusMode || videoOptionsOpen ? buildBoardVideoPrompt(board, episode, directorRequest, videoRefs, previousSummary, presentationMode, videoReferenceLimit) : ""),
    [board, directorRequest, episode, focusMode, previousSummary, presentationMode, videoOptionsOpen, videoReferenceLimit, videoRefs]
  );
  const videoPrompt = focusMode || videoOptionsOpen ? firstText(draftState.prompt, defaultVideoPrompt) : "";
  const focusGridRef = useRef(null);
  const promptEditorRef = useRef(null);
  const promptAtNodeRef = useRef(null);
  const promptAtIndexRef = useRef(-1);
  const [mentionPopover, setMentionPopover] = useState(null);
  const [imagePromptOpen, setImagePromptOpen] = useState(false);
  const [imagePromptEditing, setImagePromptEditing] = useState(false);
  const [imagePromptDraft, setImagePromptDraft] = useState(board.prompt || "");
  const [imagePromptSync, setImagePromptSync] = useState({ prompt: board.prompt, editing: false });
  if (!imagePromptEditing && (board.prompt !== imagePromptSync.prompt || imagePromptSync.editing)) {
    setImagePromptSync({ prompt: board.prompt, editing: imagePromptEditing });
    setImagePromptDraft(board.prompt || "");
  }
  const [imagePromptCopied, setImagePromptCopied] = useState(false);
  const [activeFocusTab, setActiveFocusTab] = useState("videoPrompt");
  const [localFocusColumnPercents, setLocalFocusColumnPercents] = useState(FOCUS_COLUMN_DEFAULTS);
  const [videoPromptEditing, setVideoPromptEditing] = useState(false);
  const [prevVideoBusy, setPrevVideoBusy] = useState(videoBusy);
  if (videoBusy !== prevVideoBusy) {
    setPrevVideoBusy(videoBusy);
    if (videoBusy) setVideoPromptEditing(false);
  }
  const focusColumnPercents = useMemo(
    () => (controlledFocusColumnPercents ? normalizeFocusColumnPercents(controlledFocusColumnPercents) : localFocusColumnPercents),
    [controlledFocusColumnPercents, localFocusColumnPercents]
  );
  const setFocusColumnPercents = (value) => {
    const next = normalizeFocusColumnPercents(value);
    if (onFocusColumnPercentsChange) {
      onFocusColumnPercentsChange(next);
      return;
    }
    setLocalFocusColumnPercents(next);
  };
  const updateDraft = (patch) => onVideoDraftChange?.(board.index, patch);
  const mentionItems = useMemo(
    () => (focusMode || videoOptionsOpen ? normalizeVideoPackageItems([...videoRefs, ...candidatePackageItems], videoReferenceLimit) : []),
    [candidatePackageItems, focusMode, videoOptionsOpen, videoReferenceLimit, videoRefs]
  );
  const filteredMentionItems = useMemo(() => {
    if (!mentionPopover) return [];
    const query = firstText(mentionPopover.searchText).toLowerCase();
    return mentionItems.filter((item) => (
      !query
      || firstText(item.name).toLowerCase().includes(query)
      || firstText(item.type).toLowerCase().includes(query)
    ));
  }, [mentionItems, mentionPopover]);

  useEffect(() => {
    if ((!focusMode && !videoOptionsOpen) || !videoPromptEditing || !promptEditorRef.current) return;
    if (document.activeElement === promptEditorRef.current) return;
    const serialized = serializeEditor(promptEditorRef.current);
    if (serialized === videoPrompt) return;
    renderPromptToEditor(promptEditorRef.current, hydratePromptReferenceChips(videoPrompt, videoRefs), theme.colors.accentPrimary, true);
  }, [focusMode, theme.colors.accentPrimary, videoOptionsOpen, videoPrompt, videoPromptEditing, videoRefs]);

  const startFocusColumnResize = (dividerIndex, event) => {
    const container = focusGridRef.current;
    if (!container) return;
    event.preventDefault();
    const containerWidth = container.clientWidth || 1;
    const startX = event.clientX;
    const startPercents = [...focusColumnPercents];
    const minPercents = FOCUS_COLUMN_MIN_WIDTHS.map((width) => Math.min(45, (width / containerWidth) * 100));

    const onMove = (moveEvent) => {
      const deltaPercent = ((moveEvent.clientX - startX) / containerWidth) * 100;
      const next = [...startPercents];
      const leftIndex = dividerIndex;
      const rightIndex = dividerIndex + 1;
      const pairTotal = startPercents[leftIndex] + startPercents[rightIndex];
      const minLeft = minPercents[leftIndex];
      const minRight = minPercents[rightIndex];
      const nextLeft = Math.max(minLeft, Math.min(pairTotal - minRight, startPercents[leftIndex] + deltaPercent));
      next[leftIndex] = nextLeft;
      next[rightIndex] = pairTotal - nextLeft;
      setFocusColumnPercents(next);
    };

    const onUp = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  };

  const renderPromptPreview = (text) => {
    const hydrated = hydratePromptReferenceChips(text, videoRefs);
    const parts = [];
    const combinedRe = /\{\{ref:([^:}]*):([^:}]*):([^}]+)\}\}|\{\{ref:([^:}]+):([^}]+)\}\}/g;
    let lastIndex = 0;
    let match;

    while ((match = combinedRe.exec(hydrated)) !== null) {
      if (match.index > lastIndex) {
        parts.push(hydrated.substring(lastIndex, match.index));
      }
      const category = match[1] !== undefined ? match[1] : "";
      const name = match[1] !== undefined ? match[2] : match[4];
      const url = match[1] !== undefined ? match[3] : match[5];
      parts.push({ category, name, url });
      lastIndex = combinedRe.lastIndex;
    }

    if (lastIndex < hydrated.length) parts.push(hydrated.substring(lastIndex));

    return (
      <div className="whitespace-pre-wrap break-words">
        {parts.map((part, index) => {
          if (typeof part === "string") return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>;
          return (
            <HoverImagePreview key={`${part.url}-${index}`} src={part.url} title={part.name} className="inline-flex align-middle mx-0.5">
              <span
                className="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] font-bold align-middle"
                style={{ borderColor: `${theme.colors.accentPrimary}44`, backgroundColor: `${theme.colors.accentPrimary}18`, color: theme.colors.accentPrimary }}
              >
                <img src={part.url} alt={part.name} className="w-4 h-4 rounded object-cover" />
                {part.name || part.category || "参考图"}
              </span>
            </HoverImagePreview>
          );
        })}
      </div>
    );
  };

  const updatePackageItems = (items) => updateDraft({ inputPackage: enrichVideoPackageItems(items, candidatePackageItems, assetMetaMap, videoReferenceLimit) });
  const removePackageItem = (url) => updatePackageItems(videoRefs.filter((item) => item.url !== url));
  const addPackageItem = (item) => updatePackageItems([...videoRefs, item]);
  const restoreDefaultPackage = () => updateDraft({
    inputPackage: enrichVideoPackageItems(defaultPackageItems, candidatePackageItems, assetMetaMap, videoReferenceLimit),
    previousSummary: buildPreviousBoardSummary(board, boards),
  });
  const savePromptFromEditor = () => updateDraft({ prompt: serializeEditor(promptEditorRef.current) });

  const handlePromptEditorInput = () => {
    savePromptFromEditor();
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      setMentionPopover(null);
      return;
    }
    const range = selection.getRangeAt(0);
    if (range.startContainer.nodeType !== Node.TEXT_NODE) {
      setMentionPopover(null);
      return;
    }
    const textBefore = range.startContainer.textContent.substring(0, range.startOffset);
    const lastAt = textBefore.lastIndexOf("@");
    if (lastAt === -1) {
      setMentionPopover(null);
      return;
    }
    const searchText = textBefore.substring(lastAt + 1);
    if (searchText.includes(" ") || searchText.includes("\n")) {
      setMentionPopover(null);
      return;
    }
    promptAtNodeRef.current = range.startContainer;
    promptAtIndexRef.current = lastAt;
    setMentionPopover({ searchText });
  };

  const handlePromptKeyDown = (event) => {
    if (event.key === "Escape" && mentionPopover) {
      event.preventDefault();
      setMentionPopover(null);
      return;
    }
    if (event.key !== "Backspace") return;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) return;
    const range = selection.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType === Node.TEXT_NODE && range.startOffset === 0) {
      const previous = node.previousSibling;
      if (previous?.dataset?.mention) {
        event.preventDefault();
        previous.remove();
        savePromptFromEditor();
      }
      return;
    }
    if (node === promptEditorRef.current) {
      const child = node.childNodes[range.startOffset - 1];
      if (child?.dataset?.mention) {
        event.preventDefault();
        child.remove();
        savePromptFromEditor();
      }
    }
  };

  const handleMentionSelect = (item) => {
    if (!promptEditorRef.current) return;
    const atNode = promptAtNodeRef.current;
    const atIndex = promptAtIndexRef.current;
    if (!atNode || atIndex < 0 || !atNode.parentNode) {
      addPackageItem(item);
      setMentionPopover(null);
      return;
    }

    const searchLength = firstText(mentionPopover?.searchText).length;
    const endOffset = Math.min(atIndex + 1 + searchLength, (atNode.textContent || "").length);
    const deleteRange = document.createRange();
    deleteRange.setStart(atNode, atIndex);
    deleteRange.setEnd(atNode, endOffset);
    deleteRange.deleteContents();

    const chip = createMentionChipSmall(item.name, item.url, theme.colors.accentPrimary, item.type || item.role || "");
    const afterNode = atNode.textContent.length > atIndex ? atNode.splitText(atIndex) : null;
    if (afterNode) {
      atNode.parentNode.insertBefore(chip, afterNode);
    } else {
      atNode.parentNode.insertBefore(chip, atNode.nextSibling);
    }
    const space = document.createTextNode("\u00A0");
    chip.after(space);
    const selection = window.getSelection();
    const newRange = document.createRange();
    newRange.setStartAfter(space);
    newRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(newRange);

    if (!videoRefs.some((ref) => ref.url === item.url)) addPackageItem(item);
    promptAtNodeRef.current = null;
    promptAtIndexRef.current = -1;
    setMentionPopover(null);
    savePromptFromEditor();
  };

  const copyImagePrompt = async () => {
    const text = imagePromptDraft || board.prompt || "";
    if (!text) return;
    try {
      await navigator.clipboard?.writeText(text);
      setImagePromptCopied(true);
      window.setTimeout(() => setImagePromptCopied(false), 1200);
    } catch {
      setImagePromptCopied(false);
    }
  };

  const regenerateWithImagePrompt = () => {
    const text = firstText(imagePromptDraft, board.prompt);
    if (!text) {
      onRegenerate?.(board);
      return;
    }
    onRegenerate?.(board, text);
  };

  const renderImagePromptPanel = (compact = false) => {
    if (!board.prompt && !isFailed) return null;
    return (
      <section
        className="rounded-md border overflow-hidden"
        style={{
          borderColor: imagePromptOpen ? `${theme.colors.accentPrimary}88` : "var(--color-border-primary)",
          backgroundColor: SURFACE.panel,
        }}
      >
        <button
          type="button"
          onClick={() => setImagePromptOpen((value) => !value)}
          className="w-full px-3 py-2.5 flex items-center justify-between gap-3 text-left cursor-pointer"
          style={{
            color: imagePromptOpen ? theme.colors.accentPrimary : "var(--color-text-primary)",
            backgroundColor: imagePromptOpen ? "var(--color-accent-light)" : SURFACE.panel,
          }}
        >
          <span className="min-w-0 inline-flex items-center gap-2">
            <FileText size={14} className="shrink-0" />
            <span className="min-w-0">
              <span className="block text-sm font-bold truncate">生图提示词</span>
              <span className="block text-[11px] leading-4 truncate" style={{ color: "var(--color-text-muted)" }}>
                {imagePromptOpen ? "可查看、复制、修改后用于本张重生" : "展开查看本张故事板生图文本"}
              </span>
            </span>
          </span>
          <span className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold">
            {imagePromptOpen ? "收起" : "展开"}
            {imagePromptOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </span>
        </button>

        {imagePromptOpen && (
          <div className="border-t p-3 space-y-2" style={{ borderColor: "var(--color-border-primary)" }}>
            <AutoGrowTextarea
              value={imagePromptDraft}
              onChange={(event) => {
                setImagePromptDraft(event.target.value);
                if (!imagePromptEditing) setImagePromptEditing(true);
              }}
              readOnly={!imagePromptEditing}
              minRows={compact ? 5 : 7}
              maxHeight={compact ? 260 : 420}
              className="w-full rounded-md border px-3 py-2 text-xs leading-5 outline-none whitespace-pre-wrap custom-scrollbar"
              style={{
                borderColor: imagePromptEditing ? `${theme.colors.accentPrimary}88` : "var(--color-border-primary)",
                backgroundColor: imagePromptEditing ? SURFACE.input : SURFACE.soft,
                color: "var(--color-text-primary)",
              }}
              placeholder="这张图还没有生成出提示词。"
            />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-[11px] leading-4" style={{ color: "var(--color-text-muted)" }}>
                修改后的文本仅用于本张重生图。
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <button
                  type="button"
                  onClick={copyImagePrompt}
                  disabled={!imagePromptDraft}
                  className="h-8 px-2 rounded-md border text-xs font-semibold inline-flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                  style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft, color: "var(--color-text-secondary)" }}
                >
                  {imagePromptCopied ? <Check size={13} /> : <Copy size={13} />}
                  {imagePromptCopied ? "已复制" : "复制"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setImagePromptDraft(board.prompt || "");
                    setImagePromptEditing(false);
                  }}
                  disabled={!imagePromptEditing}
                  className="h-8 px-2 rounded-md border text-xs font-semibold cursor-pointer disabled:opacity-50"
                  style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft, color: "var(--color-text-secondary)" }}
                >
                  还原
                </button>
                <button
                  type="button"
                  onClick={() => setImagePromptEditing((value) => !value)}
                  className="h-8 px-2 rounded-md border text-xs font-semibold inline-flex items-center gap-1.5 cursor-pointer"
                  style={{
                    borderColor: imagePromptEditing ? theme.colors.accentPrimary : "var(--color-border-primary)",
                    backgroundColor: imagePromptEditing ? "var(--color-accent-light)" : SURFACE.soft,
                    color: imagePromptEditing ? theme.colors.accentPrimary : "var(--color-text-secondary)",
                  }}
                >
                  <Edit3 size={13} />
                  {imagePromptEditing ? "退出编辑" : "编辑"}
                </button>
                <button
                  type="button"
                  onClick={regenerateWithImagePrompt}
                  disabled={disabled || regenerating || videoBusy || !board.index || status === "pending" || !imagePromptDraft}
                  className="h-8 px-2 rounded-md border text-xs font-bold inline-flex items-center gap-1.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ borderColor: theme.colors.accentPrimary, backgroundColor: theme.colors.accentPrimary, color: "var(--color-accent-contrast)" }}
                >
                  {regenerating ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                  用它重生图
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    );
  };

  const boardNo = String(Number(board.index) || 0).padStart(2, "0");
  const timelineColor = isFailed
    ? "var(--color-error)"
    : videoBusy
      ? theme.colors.accentPrimary
      : videoSuccess
        ? "var(--color-success)"
        : isSuccess
          ? "var(--color-success)"
          : "var(--color-text-muted)";
  const timelineStatus = videoSuccess
    ? "视频完成"
    : videoBusy
      ? "视频生成"
      : isSuccess
        ? "故事板完成"
        : isFailed
          ? "生成失败"
          : "等待生成";
  const softDividerColor = "color-mix(in srgb, var(--color-border-primary) 42%, transparent)";
  const formatHistoryTime = (value) => {
    const time = value ? Date.parse(value) : 0;
    if (!time) return "";
    const date = new Date(time);
    return `${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")} ${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
  };
  const imageHistoryItems = useMemo(() => {
    const current = boardImageUrl ? [{
      id: `current-image-${board.index}`,
      boardIndex: Number(board.index),
      imageUrl: boardImageUrl,
      url: boardImageUrl,
      title: board.title || `故事板页 ${boardNo}`,
      prompt: board.prompt || "",
      segmentText: board.segmentText || "",
      timeRange: board.timeRange || "",
      label: "当前",
      createdAt: board.updatedAt || board.createdAt,
    }] : [];
    const seen = new Set();
    return [...current, ...(Array.isArray(boardImageHistory) ? boardImageHistory : [])]
      .filter((item) => firstText(item?.imageUrl, item?.url))
      .filter((item) => {
        const key = firstText(item?.imageUrl, item?.url);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [board, boardImageHistory, boardImageUrl, boardNo]);
  const videoHistoryItems = useMemo(() => {
    const current = videoUrl ? [{
      ...(videoTask || {}),
      id: `current-video-${board.index}`,
      boardIndex: Number(board.index),
      videoUrl,
      url: videoUrl,
      label: "当前",
      createdAt: videoTask?.completedAt || videoTask?.updatedAt || videoTask?.createdAt,
    }] : [];
    const seen = new Set();
    return [...current, ...(Array.isArray(videoTask?.history) ? videoTask.history : [])]
      .filter((item) => firstText(item?.videoUrl, item?.url))
      .filter((item) => {
        const key = firstText(item?.videoUrl, item?.url);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [board.index, videoTask, videoUrl]);

  if (focusMode) {
    return (
      <article
        className="h-full min-h-0 rounded-xl border overflow-hidden flex flex-col animate-in fade-in slide-in-from-bottom-2 duration-300"
        style={{
          borderColor: videoBusy ? theme.colors.accentPrimary : "var(--color-border-primary)",
          backgroundColor: SURFACE.card,
          boxShadow: videoBusy ? `0 0 0 1px ${theme.colors.accentPrimary}24, var(--shadow-card-hover)` : "var(--shadow-card)",
        }}
      >
        <header className="shrink-0 px-3 py-2.5 border-b flex flex-wrap items-center justify-between gap-3" style={{ borderColor: softDividerColor, backgroundColor: SURFACE.panel }}>
          <div className="min-w-0 flex items-center gap-3">
            <span
              className="w-10 h-10 rounded-lg border flex items-center justify-center text-sm font-black shrink-0"
              style={{ borderColor: timelineColor, color: timelineColor, backgroundColor: SURFACE.soft }}
            >
              {boardNo}
            </span>
            <div className="min-w-0">
              <div className="text-base font-bold truncate" style={{ color: "var(--color-text-primary)" }}>
                {board.title || `故事板页 ${boardNo}`}
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs" style={{ color: "var(--color-text-muted)" }}>
                <span>{board.timeRange || `${duration}s`}</span>
                <span>·</span>
                <span>{duration} 秒</span>
                <span>·</span>
                <span style={{ color: timelineColor }}>{timelineStatus}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            <button
              type="button"
              onClick={() => onRegenerate?.(board)}
              disabled={disabled || regenerating || videoBusy || !board.index || status === "pending"}
              className="h-8 px-2.5 rounded-md border text-xs font-semibold inline-flex items-center gap-1.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderColor: theme.colors.accentPrimary, backgroundColor: "var(--color-accent-light)", color: theme.colors.accentPrimary }}
            >
              {regenerating ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
              重生图
            </button>
            {showGuaAssetUpload && (
              <button
                type="button"
                onClick={() => onUploadBoardAsset?.(board)}
                disabled={!boardImageUrl || boardAssetUploading || boardAssetProcessing || boardAssetActive}
                className="h-8 px-2.5 rounded-md border text-xs font-semibold inline-flex items-center gap-1.5 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  borderColor: boardAssetActive ? "var(--color-success)" : theme.colors.accentPrimary,
                  backgroundColor: boardAssetActive ? "rgba(34,197,94,0.12)" : "var(--color-accent-light)",
                  color: boardAssetActive ? "var(--color-success)" : theme.colors.accentPrimary,
                }}
                title={boardEffectiveAssetMeta.assetId || `把本页故事板提交到${assetLibraryLabel(videoProvider)}`}
              >
                {boardAssetUploading || boardAssetProcessing ? <Loader2 size={13} className="animate-spin" /> : boardAssetActive ? <Check size={13} /> : <PackagePlus size={13} />}
                {boardAssetUploading ? "入库中" : boardAssetProcessing ? "处理中" : boardAssetActive ? "已入库" : "入库"}
              </button>
            )}
            <button
              type="button"
              onClick={() => onGenerateVideo?.(board)}
              disabled={disabled || regenerating || videoBusy || !isSuccess || !boardImageUrl}
              className="h-8 px-3 rounded-md border text-xs font-bold inline-flex items-center gap-1.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderColor: theme.colors.accentPrimary, backgroundColor: theme.colors.accentPrimary, color: "var(--color-accent-contrast)" }}
            >
              {videoBusy ? <Loader2 size={13} className="animate-spin" /> : <Film size={13} />}
              {videoSuccess ? "重生视频" : "生成本页视频"}
            </button>
            {videoBusy && (
              <button
                type="button"
                onClick={() => onStopVideo?.(board)}
                className="h-8 px-2.5 rounded-md border text-xs font-bold inline-flex items-center gap-1.5 cursor-pointer"
                style={{ borderColor: "var(--color-warning)", backgroundColor: "rgba(245,158,11,0.12)", color: "var(--color-warning)" }}
              >
                <CircleStop size={13} />
                停止等待
              </button>
            )}
          </div>
        </header>

        <div
          ref={focusGridRef}
          className="flex-1 min-h-0 p-3 grid grid-cols-1 2xl:grid-cols-[var(--focus-grid-columns)] gap-3 items-start 2xl:items-stretch 2xl:auto-rows-[minmax(0,1fr)] overflow-x-auto overflow-y-auto 2xl:overflow-y-hidden custom-scrollbar"
          style={{
            "--focus-grid-columns": `minmax(${FOCUS_COLUMN_MIN_WIDTHS[0]}px, ${focusColumnPercents[0]}fr) 8px minmax(${FOCUS_COLUMN_MIN_WIDTHS[1]}px, ${focusColumnPercents[1]}fr) 8px minmax(${FOCUS_COLUMN_MIN_WIDTHS[2]}px, ${focusColumnPercents[2]}fr)`,
          }}
        >
          <section className="h-full min-h-0 rounded-xl border overflow-hidden min-w-0 flex flex-col" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
            <div className="shrink-0 px-3 py-2 border-b flex items-center justify-between gap-3" style={{ borderColor: "var(--color-border-primary)" }}>
              <div>
                <div className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>故事板整图</div>
	                <div className="text-xs mt-0.5" style={{ color: "var(--color-text-muted)" }}>完整展示整张图，不裁切。</div>
              </div>
              {((isSuccess && boardImageUrl) || imageHistoryItems.length > 0) && (
                <div className="flex items-center gap-2 shrink-0">
                  {(imageHistoryItems.length > 1 || !boardImageUrl) && (
                    <label className="h-8 rounded-md border px-2 inline-flex items-center gap-1.5" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft, color: "var(--color-text-secondary)" }}>
                      <History size={13} />
                      <AppSelect
                        value={boardImageUrl || ""}
                        onChange={(event) => {
                          const selected = imageHistoryItems.find((item) => firstText(item.imageUrl, item.url) === event.target.value);
                          if (selected) onSelectBoardImageHistory?.(board, selected);
                        }}
                        className="bg-transparent outline-none text-xs font-semibold cursor-pointer max-w-[150px]"
                        style={{ color: "var(--color-text-secondary)" }}
                      >
                        {!boardImageUrl && <option value="" disabled>选择历史整图</option>}
                        {imageHistoryItems.map((item, index) => {
                          const url = firstText(item.imageUrl, item.url);
                          const time = formatHistoryTime(item.createdAt);
                          return (
                            <option key={`${url}-${index}`} value={url} style={{ backgroundColor: SURFACE.panel, color: "var(--color-text-primary)" }}>
                              {index === 0 ? "当前" : `历史${index}`} {time}
                            </option>
                          );
                        })}
                      </AppSelect>
                    </label>
                  )}
                  {boardImageUrl && (
                    <button
                      type="button"
                      onClick={() => onPreview?.({ url: boardImageUrl, title: board.title || `故事板页 ${boardNo}`, boardIndex: Number(board.index) })}
                      className="h-8 px-2.5 rounded-md border text-xs font-semibold inline-flex items-center gap-1.5 cursor-pointer"
                      style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft, color: "var(--color-text-secondary)" }}
                    >
                      <ZoomIn size={13} />
                      放大
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="p-3 flex-1 min-h-0">
	              <div
	                className="w-full h-full rounded-lg border overflow-hidden relative min-h-[280px]"
	                style={{ borderColor: "var(--color-border-primary)", backgroundColor: "#08090d" }}
	              >
                {isSuccess && boardImageUrl ? (
                  <HoverImagePreview src={boardImageUrl} title={board.title || `故事板页 ${boardNo}`} className="ui-generation-result-enter block w-full h-full">
                    <img src={boardImageUrl} alt={board.title || `故事板页 ${boardNo}`} className="w-full h-full object-contain" />
                  </HoverImagePreview>
                ) : isFailed ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center" style={{ color: "var(--color-error)" }}>
                    <AlertTriangle size={30} />
                    <div className="text-sm font-bold">本页生成失败</div>
                    <div className="text-xs leading-5 max-w-[520px]">{board.error || "可以重新发送导演要求，失败页会保留占位。"}</div>
                  </div>
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2" style={{ color: "var(--color-text-muted)" }}>
                    <Loader2 size={28} className="animate-spin" />
                    <div className="text-sm font-semibold">排队生成中</div>
                  </div>
                )}
                {regenerating && (
                  <div className="absolute inset-0 flex items-center justify-center gap-2 text-sm font-medium animate-in fade-in duration-150" style={{ backgroundColor: "var(--overlay-medium)", color: "#fff" }}>
                    <Loader2 size={16} className="animate-spin" />
                    重新生成中
                  </div>
                )}
              </div>
	            </div>
	          </section>

            <div
              className="hidden 2xl:flex h-full min-h-[280px] items-center justify-center cursor-col-resize group/resize"
              onPointerDown={(event) => startFocusColumnResize(0, event)}
              role="separator"
              aria-orientation="vertical"
              title="拖动调整宽度"
            >
              <span className="w-1 h-16 rounded-full transition-colors" style={{ backgroundColor: "var(--color-border-primary)" }} />
            </div>

	          <section className="group/video-card h-full min-h-0 rounded-xl border overflow-hidden min-w-0 flex flex-col" style={{
	            borderColor: videoFailed ? "var(--color-error)" : videoSuccess ? "var(--color-success)" : videoStopped ? "var(--color-warning)" : "var(--color-border-primary)",
	            backgroundColor: "var(--color-bg-overlay)",
	          }}>
	            <div className="flex-1 min-h-0 flex flex-col">
	              <div className="relative w-full h-full min-h-[280px] overflow-hidden flex items-center justify-center" style={{ backgroundColor: "var(--color-bg-overlay)" }}>
                {videoSuccess ? (
                  <video src={videoUrl} controls className="ui-generation-result-enter w-full h-full object-contain" style={{ backgroundColor: "#000" }} />
                ) : videoFailed ? (
	                  <div className="w-full h-full min-h-[180px] flex flex-col items-center justify-center gap-2 px-5 text-center" style={{ color: "var(--color-error)" }}>
                    <AlertTriangle size={28} />
                    <div className="text-sm font-bold">视频生成失败</div>
                    <div className="text-xs leading-5 whitespace-pre-wrap break-words max-w-full">{videoTask?.error || "服务商返回失败，可以调整参数后重新提交。"}</div>
                  </div>
                ) : videoStopped ? (
	                  <div className="w-full h-full min-h-[180px] flex flex-col items-center justify-center gap-2 px-5 text-center" style={{ color: "var(--color-warning)" }}>
                    <CircleStop size={28} />
                    <div className="text-sm font-bold">已停止等待</div>
                    <div className="text-xs leading-5 whitespace-pre-wrap break-words max-w-full">{videoTask?.error || "前端已停止轮询；需要时可以重新提交本页视频。"}</div>
                  </div>
                ) : videoBusy ? (
	                  <div className="w-full h-full min-h-[180px] p-5 flex flex-col justify-center gap-4" style={{ color: "var(--color-text-primary)" }}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 text-sm font-bold">
                        <Loader2 size={17} className="animate-spin" />
                        正在生成
                      </div>
                      <button
                        type="button"
                        onClick={() => onStopVideo?.(board)}
                        className="h-8 px-2 rounded-md border text-xs font-bold inline-flex items-center gap-1.5 cursor-pointer shrink-0"
                        style={{ borderColor: "var(--color-warning)", backgroundColor: "rgba(245,158,11,0.12)", color: "var(--color-warning)" }}
                      >
                        <CircleStop size={13} />
                        停止
                      </button>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: "var(--color-border-primary)" }}>
                      <div
                        className="h-full transition-all"
                        style={{ width: `${Math.max(5, Math.min(100, Number(videoTask?.progress) || 8))}%`, backgroundColor: theme.colors.accentPrimary }}
                      />
                    </div>
                    <div className="text-xs" style={{ color: "var(--color-text-muted)" }}>完成后会在这里播放当前故事板页对应视频。</div>
                  </div>
                ) : (
	                  <div className="w-full h-full min-h-[180px] flex flex-col items-center justify-center gap-2 px-5 text-center" style={{ color: "var(--color-text-muted)" }}>
                    <Film size={30} />
                    <div className="text-sm font-bold" style={{ color: "var(--color-text-secondary)" }}>尚未生成视频</div>
                    <div className="text-xs leading-5">确认输入包、视频提示词和参数后，点击“生成本页视频”。</div>
                  </div>
                )}
                {videoSuccess && (
                  <div className="pointer-events-none absolute inset-x-0 top-0 z-10 p-3 opacity-0 transition-opacity duration-200 group-hover/video-card:opacity-100 group-focus-within/video-card:opacity-100" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.42) 54%, transparent 100%)" }}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-bold inline-flex items-center gap-2" style={{ color: "var(--color-success)" }}>
                          <CheckCircle2 size={15} />
                          本页视频预览
                        </div>
                        <div className="mt-1 text-xs truncate" style={{ color: "rgba(255,255,255,0.72)" }}>
                          {videoProviderLabel} · {resolution} · {ratio === "adaptive" ? "自适应" : ratio} · {duration}s · {videoRefs.length}张参考
                        </div>
                        {videoTask?.taskId && (
                          <div className="mt-0.5 text-[11px] truncate max-w-[220px]" style={{ color: "rgba(255,255,255,0.48)" }}>
                            任务 {videoTask.taskId}
                          </div>
                        )}
                      </div>
                      <div className="pointer-events-auto flex flex-wrap items-center justify-end gap-2 shrink-0">
                        {videoHistoryItems.length > 1 && (
                          <label className="h-8 rounded-md border px-2 inline-flex items-center gap-1.5" style={{ borderColor: "transparent", backgroundColor: "var(--overlay-medium)", color: "#fff" }}>
                            <History size={13} />
                            <AppSelect
                              value={videoUrl}
                              onChange={(event) => {
                                const selected = videoHistoryItems.find((item) => firstText(item.videoUrl, item.url) === event.target.value);
                                if (selected) onSelectVideoHistory?.(board, selected);
                              }}
                              className="bg-transparent outline-none text-xs font-medium cursor-pointer max-w-[142px]"
                              style={{ color: "#fff" }}
                            >
                              {videoHistoryItems.map((item, index) => {
                                const url = firstText(item.videoUrl, item.url);
                                const time = formatHistoryTime(item.createdAt);
                                return (
                                  <option key={`${url}-${index}`} value={url} style={{ backgroundColor: SURFACE.panel, color: "var(--color-text-primary)" }}>
                                    {index === 0 ? "当前" : `历史${index}`} {time}
                                  </option>
                                );
                              })}
                            </AppSelect>
                          </label>
                        )}
                        {videoUrl && (
                          <button
                            type="button"
                            onClick={() => onDownloadVideoHistory?.(
                              board,
                              videoHistoryItems.find((item) => firstText(item.videoUrl, item.url) === videoUrl) || { videoUrl, url: videoUrl }
                            )}
                            className="h-8 px-2 rounded-md border text-xs font-medium inline-flex items-center gap-1.5 cursor-pointer"
                            style={{ borderColor: "transparent", backgroundColor: "var(--overlay-medium)", color: "#fff" }}
                          >
                            <Download size={13} />
                            下载
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => onEraseSubtitle?.(board)}
                          disabled={subtitleBusy || !videoTask?.originalVideoUrl}
                          className="h-8 px-2 rounded-md border text-xs font-medium inline-flex items-center gap-1.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ borderColor: "transparent", backgroundColor: "var(--overlay-medium)", color: "#fff" }}
                        >
                          {subtitleBusy ? <Loader2 size={13} className="animate-spin" /> : <RemoveFormatting size={13} />}
                          {subtitleBusy ? "去字幕中" : "去字幕"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {!videoSuccess && videoHistoryItems.length > 0 && (
                  <label className="absolute right-3 top-3 z-20 h-8 rounded-md border px-2 inline-flex items-center gap-1.5" style={{ borderColor: "transparent", backgroundColor: "var(--overlay-medium)", color: "#fff" }}>
                    <History size={13} />
                    <AppSelect
                      value=""
                      onChange={(event) => {
                        const selected = videoHistoryItems.find((item) => firstText(item.videoUrl, item.url) === event.target.value);
                        if (selected) onSelectVideoHistory?.(board, selected);
                      }}
                      className="bg-transparent outline-none text-xs font-semibold cursor-pointer max-w-[150px]"
                      style={{ color: "#fff" }}
                    >
                      <option value="" disabled style={{ backgroundColor: SURFACE.panel, color: "var(--color-text-primary)" }}>查看旧视频</option>
                      {videoHistoryItems.map((item, index) => {
                        const url = firstText(item.videoUrl, item.url);
                        return (
                          <option key={`${url}-${index}`} value={url} style={{ backgroundColor: SURFACE.panel, color: "var(--color-text-primary)" }}>
                            历史{index + 1} {formatHistoryTime(item.createdAt)}
                          </option>
                        );
                      })}
                    </AppSelect>
                  </label>
                )}
              </div>
            </div>
	          </section>

            <div
              className="hidden 2xl:flex h-full min-h-[280px] items-center justify-center cursor-col-resize group/resize"
              onPointerDown={(event) => startFocusColumnResize(1, event)}
              role="separator"
              aria-orientation="vertical"
              title="拖动调整宽度"
            >
              <span className="w-1 h-16 rounded-full transition-colors" style={{ backgroundColor: "var(--color-border-primary)" }} />
            </div>

	          <aside
	            className="h-full min-h-0 rounded-xl border p-3 min-w-0 flex flex-col"
	            style={{
	              borderColor: "var(--color-border-primary)",
	              backgroundColor: SURFACE.panel,
	            }}
	          >
		            <div className="flex gap-1 rounded-lg border p-1 shrink-0 overflow-x-auto custom-scrollbar" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card }}>
		              {FOCUS_DETAIL_TABS.map((tab) => {
		                const active = activeFocusTab === tab.id;
		                return (
		                  <button
		                    key={tab.id}
		                    type="button"
		                    onClick={() => setActiveFocusTab(tab.id)}
		                    className="h-8 min-w-[74px] flex-1 rounded-md inline-flex items-center justify-center text-[11px] font-bold cursor-pointer transition-colors whitespace-nowrap"
		                    style={{
		                      backgroundColor: active ? "var(--color-accent-light)" : "transparent",
		                      color: active ? theme.colors.accentPrimary : "var(--color-text-muted)",
	                    }}
	                  >
	                    {tab.label}
	                  </button>
	                );
	              })}
	            </div>

	            <div className="mt-3 min-h-0 flex-1 overflow-y-auto custom-scrollbar space-y-3 pr-0.5">
	              {activeFocusTab === "boardPrompt" && (
	                <>
	                  <section className="rounded-lg border p-3 space-y-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card }}>
	                    <div className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>本页剧本文字</div>
	                    <div className="rounded-md border p-3 text-xs leading-5 max-h-[180px] overflow-y-auto custom-scrollbar whitespace-pre-wrap" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft, color: "var(--color-text-secondary)" }}>
	                      {board.segmentText || "本页对应一个连续故事板段落，生成完成后会显示当前段落原剧本文字。"}
	                    </div>
	                  </section>
	                  {renderImagePromptPanel(true) || (
	                    <section className="rounded-lg border p-4 text-sm" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card, color: "var(--color-text-muted)" }}>
	                      本页还没有可编辑的生图提示词。
	                    </section>
	                  )}
	                </>
	              )}

	              {activeFocusTab === "videoPrompt" && (
	                <>
	                  <section className="rounded-lg border overflow-hidden" style={{ borderColor: theme.colors.accentPrimary, backgroundColor: SURFACE.card }}>
	                    <div className="px-3 py-2.5 flex items-center justify-between gap-2" style={{ color: theme.colors.accentPrimary, backgroundColor: "var(--color-accent-light)" }}>
	                      <span className="inline-flex items-center gap-2 text-sm font-semibold">
	                        <FileText size={14} />
	                        视频提示词
	                      </span>
	                      <span className="rounded px-1.5 py-0.5 text-[10px] font-bold" style={{ backgroundColor: SURFACE.card, color: videoPromptEditing ? theme.colors.accentPrimary : "var(--color-text-muted)" }}>
	                        {videoPromptEditing ? "编辑中" : "查看"}
	                      </span>
	                    </div>
	                    <div className="px-3 pb-3 space-y-2">
	                      {videoPromptEditing ? (
	                        <div className="relative">
	                          <div
	                            ref={promptEditorRef}
	                            contentEditable={!videoBusy}
	                            suppressContentEditableWarning
	                            onInput={handlePromptEditorInput}
	                            onKeyDown={handlePromptKeyDown}
	                            onPaste={(event) => {
	                              const html = event.clipboardData.getData("text/html");
	                              if (html && html.includes("data-mention")) return;
	                              event.preventDefault();
	                              document.execCommand("insertText", false, event.clipboardData.getData("text/plain"));
	                            }}
	                            className="w-full rounded-lg border px-3 py-2.5 text-[13px] leading-6 outline-none min-h-[260px] max-h-[420px] overflow-y-auto whitespace-pre-wrap break-words custom-scrollbar"
	                            style={{ borderColor: theme.colors.accentPrimary, backgroundColor: videoBusy ? SURFACE.soft : SURFACE.input, color: "var(--color-text-primary)", opacity: videoBusy ? 0.68 : 1 }}
	                          />
	                          {mentionPopover && filteredMentionItems.length > 0 && (
	                            <div className="absolute left-2 top-full mt-1 z-50 w-72 max-h-72 overflow-y-auto rounded-md border p-1 shadow-xl custom-scrollbar animate-in fade-in zoom-in-95 duration-150" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
	                              {filteredMentionItems.map((item, itemIndex) => (
	                                <button
	                                  key={`${item.url}-${itemIndex}`}
	                                  type="button"
	                                  onMouseDown={(event) => {
	                                    event.preventDefault();
	                                    handleMentionSelect(item);
	                                  }}
	                                  className="w-full rounded-md px-2 py-2 flex items-center gap-2 text-left cursor-pointer"
	                                  style={{ color: "var(--color-text-secondary)" }}
	                                >
	                                  <HoverImagePreview src={item.url} title={item.name} className="block shrink-0">
	                                    <img src={item.url} alt={item.name} className="w-10 h-8 rounded object-cover shrink-0" />
	                                  </HoverImagePreview>
	                                  <span className="min-w-0">
	                                    <span className="block text-xs font-bold truncate" style={{ color: "var(--color-text-primary)" }}>{item.name}</span>
	                                    <span className="block text-[11px] truncate" style={{ color: "var(--color-text-muted)" }}>@ 插入到提示词，并加入输入包</span>
	                                  </span>
	                                </button>
	                              ))}
	                            </div>
	                          )}
	                        </div>
	                      ) : (
	                        <div
	                          className="w-full rounded-lg border px-3 py-2.5 text-[13px] leading-6 min-h-[260px] max-h-[420px] overflow-y-auto custom-scrollbar"
	                          style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft, color: "var(--color-text-primary)" }}
	                        >
	                          {renderPromptPreview(videoPrompt || "当前还没有视频提示词。")}
	                        </div>
	                      )}
	                      <div className="flex flex-wrap gap-2">
	                        {videoPromptEditing ? (
	                          <button
	                            type="button"
	                            onClick={() => {
	                              savePromptFromEditor();
	                              setVideoPromptEditing(false);
	                              setMentionPopover(null);
	                            }}
	                            disabled={videoBusy}
	                            className="h-8 px-2 rounded-md border text-xs font-semibold cursor-pointer disabled:opacity-50"
	                            style={{ borderColor: theme.colors.accentPrimary, backgroundColor: "var(--color-accent-light)", color: theme.colors.accentPrimary }}
	                          >
	                            保存并收起
	                          </button>
	                        ) : (
	                          <button
	                            type="button"
	                            onClick={() => setVideoPromptEditing(true)}
	                            disabled={videoBusy}
	                            className="h-8 px-2 rounded-md border text-xs font-semibold inline-flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
	                            style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft, color: "var(--color-text-secondary)" }}
	                          >
	                            <Edit3 size={13} />
	                            编辑
	                          </button>
	                        )}
	                        <button
	                          type="button"
	                          onClick={() => {
	                            updateDraft({ prompt: "" });
	                            if (promptEditorRef.current) {
	                              renderPromptToEditor(promptEditorRef.current, hydratePromptReferenceChips(defaultVideoPrompt, videoRefs), theme.colors.accentPrimary, true);
	                            }
	                          }}
	                          disabled={videoBusy || !videoPromptEditing}
	                          className="h-8 px-2 rounded-md border text-xs font-semibold cursor-pointer disabled:opacity-50"
	                          style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft, color: "var(--color-text-muted)" }}
	                        >
	                          还原默认
	                        </button>
	                        <button
	                          type="button"
	                          onClick={() => onGenerateVideo?.(board)}
	                          disabled={disabled || regenerating || videoBusy || !isSuccess || !boardImageUrl}
	                          className="ml-auto h-8 px-3 rounded-md border text-xs font-bold inline-flex items-center gap-1.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
	                          style={{ borderColor: theme.colors.accentPrimary, backgroundColor: theme.colors.accentPrimary, color: "var(--color-accent-contrast)" }}
	                        >
	                          {videoBusy ? <Loader2 size={13} className="animate-spin" /> : <Film size={13} />}
	                          用此生成视频
	                        </button>
	                      </div>
	                    </div>
	                  </section>

	                  <section className="rounded-lg border p-3 space-y-3" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card }}>
	                    <div>
	                      <div className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>生成设置</div>
	                      <div className="text-xs mt-0.5" style={{ color: "var(--color-text-muted)" }}>只影响当前故事板页。</div>
	                    </div>
	                    <div className="grid grid-cols-2 gap-2">
	                      <div className="rounded-md border p-3 space-y-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft }}>
	                        <div className="flex items-center justify-between gap-2">
	                          <span className="text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>时长</span>
	                          <span className="text-xs font-mono px-2 py-0.5 rounded" style={{ backgroundColor: SURFACE.card, color: theme.colors.accentPrimary }}>{duration}s</span>
	                        </div>
	                        <input
	                          type="range"
	                          min={isSeedance25 ? seedance25Capabilities.minDuration : 1}
	                          max={isSeedance25 ? seedance25Capabilities.maxDuration : 15}
	                          step={1}
	                          value={duration}
	                          onChange={(event) => updateDraft({
	                            duration: isSeedance25
	                              ? normalizeSeedance25Duration(videoProvider, event.target.value)
	                              : normalizeVideoDuration(event.target.value),
	                          })}
	                          disabled={videoBusy}
	                          className="w-full h-1.5 rounded-full appearance-none cursor-pointer disabled:opacity-50"
	                          style={{ accentColor: theme.colors.accentPrimary }}
	                        />
	                      </div>
	                      <div className="rounded-md border p-3 space-y-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft }}>
	                        <div className="text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>画幅</div>
	                        <AppSelect
	                          value={ratio}
	                          onChange={(event) => updateDraft({ ratio: event.target.value })}
	                          disabled={videoBusy}
	                          className="w-full h-8 rounded-md border px-2 text-xs outline-none"
	                          style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card, color: "var(--color-text-primary)" }}
	                        >
	                          {(isSeedance25 ? seedance25Capabilities.ratios : GUA_VIDEO_RATIOS).map((item) => (
	                            <option key={item} value={item}>{item === "adaptive" ? "自适应" : item}</option>
	                          ))}
	                        </AppSelect>
	                      </div>
	                      <div className="rounded-md border p-3 space-y-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft }}>
	                        <div className="text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>清晰度</div>
	                        <div className="flex gap-1 p-1 rounded-md" style={{ backgroundColor: SURFACE.card }}>
	                          {videoResolutionOptions.map((item) => (
	                            <button
	                              key={item}
	                              type="button"
	                              onClick={() => updateDraft({ resolution: item })}
	                              disabled={videoBusy || videoResolutionOptions.length <= 1}
	                              className="flex-1 h-7 rounded-md text-xs font-semibold cursor-pointer disabled:opacity-50"
	                              style={{ backgroundColor: resolution === item ? theme.colors.accentPrimary : "transparent", color: resolution === item ? "#fff" : "var(--color-text-secondary)" }}
	                            >
	                              {item}
	                            </button>
	                          ))}
	                        </div>
	                      </div>
	                      <div className="rounded-md border p-3 space-y-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft }}>
	                        <div className="text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>开关</div>
	                        <div className="flex gap-1">
	                          {!hasAudioControl ? (
	                            <span className="flex-1 h-8 rounded-md border text-xs font-bold inline-flex items-center justify-center gap-1" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card, color: "var(--color-text-muted)" }}>
	                              <Volume2 size={12} /> 平台自动
	                            </span>
	                          ) : (
	                            <button
	                              type="button"
	                              onClick={() => updateDraft({ generateAudio: !generateAudio })}
	                              disabled={videoBusy}
	                              className="flex-1 h-8 rounded-md border text-xs font-bold inline-flex items-center justify-center gap-1 cursor-pointer disabled:opacity-50"
	                              style={{ borderColor: generateAudio ? theme.colors.accentPrimary : "var(--color-border-primary)", backgroundColor: generateAudio ? "var(--color-accent-light)" : SURFACE.card, color: generateAudio ? theme.colors.accentPrimary : "var(--color-text-muted)" }}
	                            >
	                              <Volume2 size={12} />
	                              有声
	                            </button>
	                          )}
	                          <button
	                            type="button"
	                            onClick={() => updateDraft({ noSubtitle: !noSubtitle })}
	                            disabled={videoBusy}
	                            className="flex-1 h-8 rounded-md border text-xs font-bold inline-flex items-center justify-center gap-1 cursor-pointer disabled:opacity-50"
	                            style={{ borderColor: noSubtitle ? theme.colors.accentPrimary : "var(--color-border-primary)", backgroundColor: noSubtitle ? "var(--color-accent-light)" : SURFACE.card, color: noSubtitle ? theme.colors.accentPrimary : "var(--color-text-muted)" }}
	                          >
	                            <RemoveFormatting size={12} />
	                            去字
	                          </button>
	                        </div>
	                      </div>
	                    </div>
	                    <div className="rounded-lg border p-3 space-y-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft }}>
	                      <div className="flex items-center gap-2 text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>
	                        <Lightbulb size={14} style={{ color: theme.colors.accentPrimary }} />
	                        打光预设
	                      </div>
	                      <div className="flex flex-wrap gap-1.5">
	                        <button
	                          type="button"
	                          onClick={() => updateDraft({ lighting: "" })}
	                          disabled={videoBusy}
	                          className="h-8 rounded-md border px-2 text-xs font-semibold cursor-pointer disabled:opacity-50"
	                          style={{ borderColor: !selectedLighting ? theme.colors.accentPrimary : "var(--color-border-primary)", backgroundColor: !selectedLighting ? "var(--color-accent-light)" : SURFACE.card, color: !selectedLighting ? theme.colors.accentPrimary : "var(--color-text-secondary)" }}
	                        >
	                          无打光
	                        </button>
	                        {LIGHTING_PRESETS.map((preset) => {
	                          const active = selectedLighting === preset.id;
	                          return (
	                            <button
	                              key={preset.id}
	                              type="button"
	                              onClick={() => updateDraft({ lighting: active ? "" : preset.id })}
	                              disabled={videoBusy}
	                              title={preset.prompt}
	                              className="h-8 rounded-md border px-2 text-xs font-semibold cursor-pointer disabled:opacity-50"
	                              style={{ borderColor: active ? preset.color : "var(--color-border-primary)", backgroundColor: active ? preset.colorSoft : SURFACE.card, color: active ? preset.color : "var(--color-text-secondary)" }}
	                            >
	                              {preset.name}
	                            </button>
	                          );
	                        })}
	                      </div>
	                    </div>
	                  </section>
	                </>
	              )}

		              {activeFocusTab === "references" && (
		                <section className="rounded-lg border p-3 space-y-3" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card }}>
	                  <div className="flex flex-wrap items-center justify-between gap-2">
	                    <div>
	                      <div className="text-sm font-bold inline-flex items-center gap-2" style={{ color: "var(--color-text-primary)" }}>
	                        <Images size={14} style={{ color: theme.colors.accentPrimary }} />
	                        本页视频输入包
	                      </div>
	                      <div className="text-xs mt-1" style={{ color: "var(--color-text-muted)" }}>第一张通常是故事板整图，后面是人物、场景、道具参考。</div>
	                    </div>
	                    <div className="flex items-center gap-2">
	                      <button
	                        type="button"
	                        onClick={() => onOpenVideoReferencePicker?.(board.index, videoRefs)}
	                        disabled={videoBusy || videoRefs.length >= videoReferenceLimit}
	                        className="h-8 px-2.5 rounded-md border text-xs font-semibold cursor-pointer disabled:opacity-50"
	                        style={{ borderColor: "var(--color-accent-primary)", backgroundColor: "var(--color-accent-light)", color: "var(--color-accent-primary)" }}
	                      >
	                        添加视频参考图
	                      </button>
	                      <button
	                        type="button"
	                        onClick={restoreDefaultPackage}
	                        disabled={videoBusy}
	                        className="h-8 px-2.5 rounded-md border text-xs font-semibold cursor-pointer disabled:opacity-50"
	                        style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft, color: "var(--color-text-secondary)" }}
	                      >
	                        恢复默认
	                      </button>
	                    </div>
	                  </div>
	                  <span className="text-xs font-mono" style={{ color: "var(--color-text-muted)" }}>{videoRefs.length}/{videoReferenceLimit}</span>

	                  {videoRefs.length ? (
	                    <div className="flex gap-2 overflow-x-auto custom-scrollbar pb-1">
	                      {videoRefs.map((ref, refIndex) => {
	                        const guaMeta = getPackageItemGuaMeta(ref, assetMetaMap, videoProvider);
	                        const isActiveAsset = guaMeta.status === "Active" && !!guaMeta.assetId;
	                        const isProcessingAsset = guaMeta.status === "Processing";
	                        const isUploadingAsset = uploadingAssetUrls?.has?.(normalizeImageUrl(ref.url));
	                        return (
	                          <div key={`${ref.url}-${refIndex}`} className="w-[132px] shrink-0 rounded-md border overflow-hidden" style={{ borderColor: isActiveAsset ? "var(--color-success)" : "var(--color-border-primary)", borderWidth: isActiveAsset ? 2 : 1, backgroundColor: SURFACE.soft }}>
	                            <HoverImagePreview src={getSeedance25ReferenceMediaType(ref) === "image" ? ref.url : ""} title={ref.name} className="block">
	                              <button type="button" onClick={() => getSeedance25ReferenceMediaType(ref) === "image" && onPreview?.({ url: ref.url, title: ref.name })} className="aspect-video w-full relative block cursor-pointer" title={ref.name}>
	                                <ReferenceMediaThumbnail item={ref} />
	                                <span className="absolute left-1.5 top-1.5 rounded px-1.5 py-0.5 text-[10px] font-bold" style={{ backgroundColor: "var(--overlay-medium)", color: "#fff" }}>@图{refIndex + 1}</span>
	                                {isActiveAsset && (
	                                  <span className="absolute right-1.5 top-1.5 w-5 h-5 rounded-md inline-flex items-center justify-center" style={{ backgroundColor: "color-mix(in srgb, var(--color-success) 90%, transparent)", color: "#fff" }} title={guaMeta.assetId || "已入库"}>
	                                    <Check size={12} strokeWidth={3} />
	                                  </span>
	                                )}
	                              </button>
	                            </HoverImagePreview>
	                            <div className="p-2 space-y-1.5">
	                              <div className="text-xs font-bold truncate" style={{ color: "var(--color-text-primary)" }}>{ref.name}</div>
	                              <div className="flex items-center gap-1">
	                                {showGuaAssetUpload && !isActiveAsset && (
	                                  <button
	                                    type="button"
	                                    onClick={() => onUploadPackageItem?.(ref)}
	                                    disabled={videoBusy || isProcessingAsset || isUploadingAsset}
	                                    className="h-7 rounded-md border px-2 inline-flex items-center gap-1 text-[11px] font-semibold cursor-pointer disabled:opacity-50"
	                                    style={{ borderColor: "var(--color-border-primary)", backgroundColor: isProcessingAsset ? "var(--color-bg-overlay)" : "var(--color-accent-light)", color: isProcessingAsset ? "var(--color-text-muted)" : theme.colors.accentPrimary }}
	                                    title={isProcessingAsset ? (guaMeta.assetId || "入库处理中") : `加入${assetLibraryLabel(videoProvider)}`}
	                                  >
	                                    {isUploadingAsset || isProcessingAsset ? <Loader2 size={12} className="animate-spin" /> : <PackagePlus size={12} />}
	                                    {isUploadingAsset ? "提交" : isProcessingAsset ? "处理中" : "入库"}
	                                  </button>
	                                )}
	                                <button
	                                  type="button"
	                                  onClick={() => removePackageItem(ref.url)}
	                                  disabled={videoBusy}
	                                  className="ml-auto w-7 h-7 rounded-md border inline-flex items-center justify-center cursor-pointer disabled:opacity-50"
	                                  style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card, color: "var(--color-text-muted)" }}
	                                  title="从本次视频输入包移除"
	                                >
	                                  <X size={13} />
	                                </button>
	                              </div>
	                            </div>
	                          </div>
	                        );
	                      })}
	                    </div>
	                  ) : (
	                    <div className="rounded-md border border-dashed p-4 text-center text-sm" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-muted)", backgroundColor: SURFACE.soft }}>
	                      当前没有图片输入。至少建议保留本页故事板。
	                    </div>
	                  )}

	                  {candidatePackageItems.some((item) => !videoRefs.some((ref) => ref.url === item.url)) && (
	                    <div className="space-y-2">
	                      <div className="text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>可加入当前页</div>
	                      <div className="flex flex-wrap gap-2">
	                        {candidatePackageItems
	                          .filter((item) => !videoRefs.some((ref) => ref.url === item.url))
	                          .slice(0, 12)
	                          .map((item) => (
	                            <button
	                              key={item.url}
	                              type="button"
	                              onClick={() => addPackageItem(item)}
	                              disabled={videoBusy || videoRefs.length >= videoReferenceLimit}
	                              className="h-8 rounded-md border px-2 inline-flex items-center gap-2 text-xs font-semibold cursor-pointer disabled:opacity-50"
	                              style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft, color: "var(--color-text-secondary)" }}
	                            >
	                              <HoverImagePreview src={item.url} title={item.name} className="block shrink-0">
	                                <img src={item.url} alt={item.name} className="w-5 h-5 rounded object-cover" />
	                              </HoverImagePreview>
	                              {item.name}
	                            </button>
	                          ))}
	                      </div>
	                    </div>
	                  )}
		                </section>
		              )}

		              {activeFocusTab === "globalRefs" && (
		                <SelectedRefsPanel
		                  selectedRefs={selectedRefs}
		                  onRemoveRef={onRemoveSelectedRef}
		                  onOpenPicker={onOpenReferencePicker}
		                  onMatchFromScript={onMatchRefsFromScript}
		                  onClear={onClearSelectedRefs}
		                  disabled={directorBusy || disabled}
	                  limit={REF_LIMIT}
		                  embedded
		                  className="h-full"
		                />
		              )}

		              {activeFocusTab === "directorChat" && (
	                <section className="rounded-lg border p-3 space-y-3" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card }}>
	                  <div>
	                    <div className="text-sm font-bold inline-flex items-center gap-2" style={{ color: "var(--color-text-primary)" }}>
	                      <MessageSquareText size={14} style={{ color: theme.colors.accentPrimary }} />
	                      导演对话
	                    </div>
	                    <div className="text-xs leading-5 mt-1" style={{ color: "var(--color-text-muted)" }}>
	                      在这里给导演补充修改意见；已有故事板时会先生成待确认动作，确认后再覆盖。
	                    </div>
	                  </div>

	                  <AutoGrowTextarea
	                    value={directorRequest}
	                    onChange={(event) => setDirectorRequest?.(event.target.value)}
	                    onKeyDown={(event) => {
	                      if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
	                        event.preventDefault();
	                        onSendDirectorMessage?.();
	                      }
	                    }}
	                    disabled={directorBusy || disabled}
	                    minRows={5}
	                    maxHeight={260}
	                    className="w-full resize-none rounded-md border px-3 py-2 text-sm leading-6 outline-none disabled:opacity-50 custom-scrollbar"
	                    style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.input, color: "var(--color-text-primary)" }}
	                    placeholder={hasGeneratedBoards ? "告诉导演要改哪里，例如：第2张情绪更压抑，镜头更低机位。" : "告诉导演整体节奏、镜头风格和重点；首次发送会生成连续故事板。"}
	                  />

	                  <div className="flex flex-wrap items-center gap-2">
	                    <button
	                      type="button"
	                      onClick={onSendDirectorMessage}
	                      disabled={directorBusy || disabled}
	                      className="h-9 px-3 rounded-md border text-xs font-bold inline-flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
	                      style={{ borderColor: theme.colors.accentPrimary, backgroundColor: theme.colors.accentPrimary, color: "var(--color-accent-contrast)" }}
	                    >
	                      {loading === "generate" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
	                      {hasGeneratedBoards ? "发送给导演" : "发送并生成"}
	                    </button>
	                    <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>支持 Cmd/Ctrl + Enter 发送</span>
	                  </div>

	                  {pendingDirectorAction && (
	                    <div className="rounded-md border px-3 py-2 space-y-2" style={{ borderColor: theme.colors.accentPrimary, backgroundColor: "var(--color-accent-light)" }}>
	                      <div className="text-xs leading-5" style={{ color: "var(--color-text-secondary)" }}>
	                        <b style={{ color: theme.colors.accentPrimary }}>
	                          {pendingDirectorAction.mode === "single"
	                            ? `待确认：只修改第${pendingDirectorAction.boardIndex}张`
	                            : pendingDirectorAction.mode === "all"
	                              ? "待确认：整集重排"
	                              : "待确认：导演备注"}
	                        </b>
	                        <span className="ml-2">{pendingDirectorAction.requestText}</span>
	                      </div>
	                      <div className="flex flex-wrap items-center gap-2">
	                        {pendingDirectorAction.boardIndex && (
	                          <button
	                            type="button"
	                            onClick={onExecutePendingSingleBoard}
	                            disabled={directorBusy || disabled}
	                            className="h-8 px-2.5 rounded-md border text-xs font-bold inline-flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
	                            style={{ borderColor: theme.colors.accentPrimary, backgroundColor: theme.colors.accentPrimary, color: "var(--color-accent-contrast)" }}
	                          >
	                            <RefreshCw size={13} />
	                            只重生第{pendingDirectorAction.boardIndex}张
	                          </button>
	                        )}
	                        <button
	                          type="button"
	                          onClick={onExecutePendingAllBoards}
	                          disabled={directorBusy || disabled}
	                          className="h-8 px-2.5 rounded-md border text-xs font-semibold cursor-pointer disabled:opacity-50"
	                          style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card, color: "var(--color-text-primary)" }}
	                        >
	                          整集重排
	                        </button>
	                        <button
	                          type="button"
	                          onClick={onKeepPendingAsNote}
	                          disabled={directorBusy || disabled}
	                          className="h-8 px-2.5 rounded-md border text-xs font-semibold cursor-pointer disabled:opacity-50"
	                          style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card, color: "var(--color-text-secondary)" }}
	                        >
	                          仅备注
	                        </button>
	                        <button
	                          type="button"
	                          onClick={onCancelPendingAction}
	                          disabled={directorBusy || disabled}
	                          className="h-8 px-2.5 rounded-md border text-xs font-semibold cursor-pointer disabled:opacity-50"
	                          style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card, color: "var(--color-text-muted)" }}
	                        >
	                          取消
	                        </button>
	                      </div>
	                    </div>
	                  )}

	                  <div className="rounded-lg border p-3 space-y-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft }}>
	                    <div className="text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>本页视频提示词中的导演补充</div>
	                    <div className="text-xs leading-6 whitespace-pre-wrap break-words" style={{ color: "var(--color-text-muted)" }}>
	                      {firstText(directorRequest)
	                        ? `生成视频时会带入：导演补充要求：${firstText(directorRequest)}`
	                        : "没有导演补充时，本页视频只按故事板、剧本、参考图和视频设置生成。"}
	                    </div>
	                  </div>
	                </section>
	              )}
	            </div>
	          </aside>
	        </div>
      </article>
    );
  }

  return (
    <div
      className={`group/storyboard relative grid grid-cols-1 ${focusMode ? "" : "md:grid-cols-[76px_minmax(0,1fr)]"} gap-3 animate-in fade-in slide-in-from-bottom-2 duration-300`}
    >
      {!focusMode && <div className="hidden md:flex relative flex-col items-center pt-1">
        <div
          className="absolute top-0 bottom-[-28px] left-1/2 w-px -translate-x-1/2"
          style={{ backgroundColor: softDividerColor }}
        />
        <div
          className="sticky top-4 z-20 flex flex-col items-center"
        >
          <div
            className="w-12 h-12 rounded-full border-2 flex flex-col items-center justify-center shadow-sm"
            style={{
              borderColor: timelineColor,
              backgroundColor: SURFACE.panel,
              color: timelineColor,
            }}
          >
            <span className="text-[10px] leading-none font-black">#{boardNo}</span>
            <span className="text-[9px] leading-none mt-0.5 opacity-80">{duration}s</span>
          </div>
          <div
            className="mt-2 rounded-md border px-2 py-1.5 w-[68px] text-center shadow-sm"
            style={{
              borderColor: softDividerColor,
              backgroundColor: SURFACE.panel,
            }}
          >
            <div className="text-[10px] font-mono leading-4" style={{ color: "var(--color-text-secondary)" }}>
              {board.timeRange || "--"}
            </div>
            <div className="text-[10px] font-bold mt-0.5" style={{ color: timelineColor }}>
              {timelineStatus}
            </div>
          </div>
        </div>
      </div>}

      <article
        className="rounded-md border overflow-hidden transition-colors"
        style={{
          borderColor: isFailed ? "var(--color-error)" : videoBusy ? theme.colors.accentPrimary : isSuccess ? "var(--color-border-primary)" : "var(--color-border-secondary)",
          backgroundColor: SURFACE.card,
          boxShadow: videoBusy
            ? `0 0 0 1px ${theme.colors.accentPrimary}24, var(--shadow-card-hover)`
            : "var(--shadow-card)",
        }}
      >
      <div>
        <div className="p-2.5 grid grid-cols-1 min-[1850px]:grid-cols-[280px_minmax(0,1fr)_240px] gap-2.5">
          <div className="space-y-2">
            <div className="aspect-video rounded-md border overflow-hidden relative" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft }}>
              {isSuccess && boardImageUrl ? (
                <HoverImagePreview src={boardImageUrl} title={board.title || `第${board.index}张故事板`} className="ui-generation-result-enter block w-full h-full">
                  <img src={boardImageUrl} alt={board.title || `故事板${board.index}`} className="w-full h-full object-contain" />
                </HoverImagePreview>
              ) : isFailed ? (
                <div className="w-full h-full flex flex-col items-center justify-center gap-2 px-4 text-center" style={{ color: "var(--color-error)" }}>
                  <AlertTriangle size={24} />
                  <div className="text-sm font-bold">本张生成失败</div>
                  <div className="text-xs leading-5 line-clamp-3">{board.error || "可以重新发送要求，失败项会保留占位。"}</div>
                </div>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-2" style={{ color: "var(--color-text-muted)" }}>
                  <Loader2 size={24} className="animate-spin" />
                  <div className="text-sm font-semibold">排队生成中</div>
                </div>
              )}
              <span className="absolute top-2 left-2 rounded-md px-2 py-1 text-xs font-bold" style={{ backgroundColor: "var(--overlay-medium)", color: "#fff" }}>
                #{board.index} · {board.timeRange}
              </span>
              {regenerating && (
                <div className="absolute inset-0 flex items-center justify-center gap-2 text-sm font-medium animate-in fade-in duration-150" style={{ backgroundColor: "var(--overlay-medium)", color: "#fff" }}>
                  <Loader2 size={16} className="animate-spin" />
                  重新生成中
                </div>
              )}
              {isSuccess && boardImageUrl && (
                <button
                  type="button"
                  onClick={() => onPreview?.({ url: boardImageUrl, title: board.title || `第${board.index}张故事板`, boardIndex: Number(board.index) })}
                  className="absolute right-2 bottom-2 h-9 px-3 rounded-md border text-sm font-semibold inline-flex items-center gap-2 cursor-pointer"
                  style={{ borderColor: "color-mix(in srgb, var(--color-accent-contrast) 35%, transparent)", backgroundColor: "var(--overlay-medium)", color: "#fff" }}
                >
                  <ZoomIn size={15} />
                  放大
                </button>
              )}
            </div>
          </div>

          <div className="min-w-0 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-base font-bold truncate" style={{ color: "var(--color-text-primary)" }}>
                  {board.title || `第${board.index}张故事板`}
                </div>
                <div className="text-xs mt-1" style={{ color: "var(--color-text-muted)" }}>
                  {duration} 秒视频段落
                </div>
              </div>
              <span
                className="shrink-0 rounded-md border px-2 py-1 text-xs font-bold inline-flex items-center gap-1"
                style={{
                  borderColor: isFailed ? "var(--color-error)" : isSuccess ? "var(--color-success)" : "var(--color-border-primary)",
                  color: isFailed ? "var(--color-error)" : isSuccess ? "var(--color-success)" : "var(--color-text-muted)",
                  backgroundColor: isFailed ? "color-mix(in srgb, var(--color-error) 10%, transparent)" : isSuccess ? "color-mix(in srgb, var(--color-success) 10%, transparent)" : SURFACE.soft,
                }}
              >
                {isFailed ? <AlertTriangle size={13} /> : isSuccess ? <CheckCircle2 size={13} /> : <Loader2 size={13} className="animate-spin" />}
                {isFailed ? "失败" : isSuccess ? "故事板已生成" : "故事板生成中"}
              </span>
            </div>

            <div className="rounded-md border p-3 text-sm leading-6" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft, color: "var(--color-text-secondary)" }}>
              {board.segmentText || "本张对应一个15秒段落，生成完成后会显示当前段落原剧本文本。"}
            </div>

            {renderImagePromptPanel(false)}

            <div className="flex flex-wrap items-center gap-1.5">
              <span className="h-7 px-2 rounded-md border text-xs inline-flex items-center" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft, color: "var(--color-text-secondary)" }}>{duration}s</span>
              <span className="h-7 px-2 rounded-md border text-xs inline-flex items-center" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft, color: "var(--color-text-secondary)" }}>{ratio === "adaptive" ? "自适应" : ratio}</span>
              <span className="h-7 px-2 rounded-md border text-xs inline-flex items-center" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft, color: "var(--color-text-secondary)" }}>{resolution}</span>
              <span className="h-7 px-2 rounded-md border text-xs inline-flex items-center" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft, color: "var(--color-text-secondary)" }}>{!hasAudioControl ? "声音平台自动" : generateAudio ? "有声" : "静音"}</span>
              <span className="h-7 px-2 rounded-md border text-xs inline-flex items-center" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft, color: "var(--color-text-secondary)" }}>本张输入包 {videoRefs.length} 张</span>
            </div>
          </div>

          <aside className="rounded-lg border p-2.5 space-y-2.5" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
            <div className="text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>本张进度</div>
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-md border px-2 py-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft }}>
                <div className="text-[10px]" style={{ color: "var(--color-text-muted)" }}>故事板</div>
                <div className="text-xs font-bold mt-0.5" style={{ color: isSuccess ? "var(--color-success)" : isFailed ? "var(--color-error)" : "var(--color-text-secondary)" }}>
                  {isSuccess ? "已生成" : isFailed ? "失败" : "等待"}
                </div>
              </div>
              <div className="rounded-md border px-2 py-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft }}>
                <div className="text-[10px]" style={{ color: "var(--color-text-muted)" }}>视频</div>
                <div className="text-xs font-bold mt-0.5" style={{ color: videoSuccess ? "var(--color-success)" : videoFailed ? "var(--color-error)" : videoStopped ? "var(--color-warning)" : "var(--color-text-secondary)" }}>
                  {videoSuccess ? "已生成" : videoFailed ? "失败" : videoStopped ? "已停止" : videoBusy ? "生成中" : "未生成"}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => onToggleVideoOptions?.(board.index)}
              className="w-full h-9 rounded-md border text-xs font-bold inline-flex items-center justify-center gap-2 cursor-pointer"
              style={{
                borderColor: videoOptionsOpen ? theme.colors.accentPrimary : `${theme.colors.accentPrimary}55`,
                backgroundColor: videoOptionsOpen ? theme.colors.accentPrimary : "var(--color-accent-light)",
                color: videoOptionsOpen ? "var(--color-accent-contrast)" : theme.colors.accentPrimary,
              }}
            >
              <Settings2 size={13} />
              {videoOptionsOpen ? "收起本张配置" : "配置本张视频"}
            </button>
          </aside>
        </div>

        <div className="p-2.5 grid grid-cols-1 min-[1850px]:grid-cols-[minmax(0,1fr)_360px] gap-2.5 items-start border-t" style={{ borderColor: softDividerColor }}>
          <section className="rounded-md border overflow-hidden" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
              <div className="px-2.5 py-2 flex flex-col gap-2" style={{ borderColor: "var(--color-border-primary)" }}>
              <button
                type="button"
                onClick={() => onToggleVideoOptions?.(board.index)}
                className="w-full min-h-10 flex items-center justify-between gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors text-left"
                style={{
                  backgroundColor: videoOptionsOpen ? theme.colors.accentPrimary : `${theme.colors.accentPrimary}15`,
                  border: `1.5px solid ${videoOptionsOpen ? theme.colors.accentPrimary : `${theme.colors.accentPrimary}40`}`,
                  color: videoOptionsOpen ? "var(--color-accent-contrast)" : theme.colors.accentPrimary,
                }}
              >
                <span className="min-w-0 flex items-center gap-2">
                  <Film size={15} className="shrink-0" />
                  <span className="min-w-0">
                    <span className="block text-sm font-bold truncate">{videoProviderLabel} 视频生成设置</span>
                    <span className="block text-[11px] leading-4 opacity-80 truncate">
                      {videoOptionsOpen ? "参数、输入包和提示词已展开" : "展开比例、打光、去字幕、输入包和提示词"}
                    </span>
                  </span>
                </span>
                <span className="shrink-0 inline-flex items-center gap-2 text-xs font-bold">
                  {videoOptionsOpen ? "收起" : "展开"}
                  {videoOptionsOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                </span>
              </button>

              <div className="flex flex-wrap items-center justify-end gap-1.5">
                <button
                  type="button"
                  onClick={() => onRegenerate?.(board)}
                  disabled={disabled || regenerating || videoBusy || !board.index || status === "pending"}
                  className="h-7 px-2 rounded-md border text-xs font-semibold inline-flex items-center gap-1 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ borderColor: theme.colors.accentPrimary, backgroundColor: "var(--color-accent-light)", color: theme.colors.accentPrimary }}
                >
                  {regenerating ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                  重生图
                </button>
                {showGuaAssetUpload && (
                  <button
                    type="button"
                    onClick={() => onUploadBoardAsset?.(board)}
                    disabled={!boardImageUrl || boardAssetUploading || boardAssetProcessing || boardAssetActive}
                    className="h-7 px-2 rounded-md border text-xs font-semibold inline-flex items-center gap-1 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{
                      borderColor: boardAssetActive ? "var(--color-success)" : theme.colors.accentPrimary,
                      backgroundColor: boardAssetActive ? "rgba(34,197,94,0.12)" : "var(--color-accent-light)",
                      color: boardAssetActive ? "var(--color-success)" : theme.colors.accentPrimary,
                    }}
                    title={boardEffectiveAssetMeta.assetId || `把本张故事板加入资源中心并提交${assetLibraryLabel(videoProvider)}`}
                  >
                    {boardAssetUploading || boardAssetProcessing ? <Loader2 size={12} className="animate-spin" /> : boardAssetActive ? <Check size={12} /> : <PackagePlus size={12} />}
                    {boardAssetUploading ? "入库中" : boardAssetProcessing ? "处理中" : boardAssetActive ? "已入库" : "入库"}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onGenerateVideo?.(board)}
                  disabled={disabled || regenerating || videoBusy || !isSuccess || !boardImageUrl}
                  className="h-7 px-2 rounded-md border text-xs font-bold inline-flex items-center gap-1 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ borderColor: theme.colors.accentPrimary, backgroundColor: theme.colors.accentPrimary, color: "var(--color-accent-contrast)" }}
                >
                  {videoBusy ? <Loader2 size={12} className="animate-spin" /> : <Film size={12} />}
                  {videoSuccess ? "重生视频" : "生视频"}
                </button>
                {videoBusy && (
                  <button
                    type="button"
                    onClick={() => onStopVideo?.(board)}
                    className="h-7 px-2 rounded-md border text-xs font-bold inline-flex items-center gap-1 cursor-pointer"
                    style={{ borderColor: "var(--color-warning)", backgroundColor: "rgba(245,158,11,0.12)", color: "var(--color-warning)" }}
                  >
                    <CircleStop size={12} />
                    停止等待
                  </button>
                )}
              </div>
            </div>

            <div className="border-t" style={{ borderColor: softDividerColor }}>
            {videoOptionsOpen ? (
              <div className="p-2.5 space-y-3 animate-in fade-in slide-in-from-top-1 duration-200">
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
                  <div className="rounded-md border p-3 space-y-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft }}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>时长</span>
                      <span className="text-xs font-mono px-2 py-0.5 rounded" style={{ backgroundColor: SURFACE.card, color: theme.colors.accentPrimary }}>{duration}s</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>{isSeedance25 ? seedance25Capabilities.minDuration : 4}</span>
                      <input
                        type="range"
                        min={isSeedance25 ? seedance25Capabilities.minDuration : 4}
                        max={isSeedance25 ? seedance25Capabilities.maxDuration : 15}
                        step={1}
                        value={duration}
                        onChange={(event) => updateDraft({
                          duration: isSeedance25
                            ? normalizeSeedance25Duration(videoProvider, event.target.value)
                            : normalizeVideoDuration(event.target.value),
                        })}
                        disabled={videoBusy}
                        className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer disabled:opacity-50"
                        style={{ accentColor: theme.colors.accentPrimary }}
                      />
                      <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>{isSeedance25 ? seedance25Capabilities.maxDuration : 15}</span>
                    </div>
                  </div>

                  <div className="rounded-md border p-3 space-y-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft }}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>有声视频</span>
                      {!hasAudioControl ? (
                        <span className="h-8 px-3 rounded-md border text-xs font-bold inline-flex items-center gap-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card, color: "var(--color-text-muted)" }}>
                          <Volume2 size={13} /> 平台自动
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => updateDraft({ generateAudio: !generateAudio })}
                          disabled={videoBusy}
                          className="h-8 px-3 rounded-md border text-xs font-bold inline-flex items-center gap-2 cursor-pointer disabled:opacity-50"
                          style={{
                            borderColor: generateAudio ? theme.colors.accentPrimary : "var(--color-border-primary)",
                            backgroundColor: generateAudio ? "var(--color-accent-light)" : SURFACE.card,
                            color: generateAudio ? theme.colors.accentPrimary : "var(--color-text-muted)",
                          }}
                        >
                          <Volume2 size={13} />
                          {generateAudio ? "开启" : "关闭"}
                        </button>
                      )}
                    </div>
                    <div className="text-xs" style={{ color: "var(--color-text-muted)" }}>{!hasAudioControl ? `${videoProviderLabel} 当前协议不提供有声开关。` : `当前使用 ${videoProviderLabel}，有声开关会随任务提交。`}</div>
                  </div>

                  <div className="rounded-md border p-3 space-y-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft }}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>去字幕</span>
                      <button
                        type="button"
                        onClick={() => updateDraft({ noSubtitle: !noSubtitle })}
                        disabled={videoBusy}
                        className="h-8 px-3 rounded-md border text-xs font-bold inline-flex items-center gap-2 cursor-pointer disabled:opacity-50"
                        style={{
                          borderColor: noSubtitle ? theme.colors.accentPrimary : "var(--color-border-primary)",
                          backgroundColor: noSubtitle ? "var(--color-accent-light)" : SURFACE.card,
                          color: noSubtitle ? theme.colors.accentPrimary : "var(--color-text-muted)",
                        }}
                      >
                        <RemoveFormatting size={13} />
                        {noSubtitle ? "开启" : "关闭"}
                      </button>
                    </div>
                    <div className="text-xs" style={{ color: "var(--color-text-muted)" }}>开启后会在提交前自动加入禁止字幕指令。</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_220px] gap-3">
                  <div className="space-y-1.5">
                    <div className="text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>画面比例</div>
                    <div className="grid grid-cols-7 gap-1 p-1 rounded-md" style={{ backgroundColor: SURFACE.soft }}>
                      {(isSeedance25 ? seedance25Capabilities.ratios : GUA_VIDEO_RATIOS).map((item) => (
                        <button
                          key={item}
                          type="button"
                          onClick={() => updateDraft({ ratio: item })}
                          disabled={videoBusy}
                          className="h-8 rounded-md text-xs font-semibold cursor-pointer disabled:opacity-50"
                          style={{
                            backgroundColor: ratio === item ? theme.colors.accentPrimary : "transparent",
                            color: ratio === item ? "#fff" : "var(--color-text-secondary)",
                          }}
                        >
                          {item === "adaptive" ? "自适应" : item}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <div className="text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>分辨率</div>
                    <div className="flex gap-1 p-1 rounded-md" style={{ backgroundColor: SURFACE.soft }}>
                      {videoResolutionOptions.map((item) => (
                        <button
                          key={item}
                          type="button"
                          onClick={() => updateDraft({ resolution: item })}
                          disabled={videoBusy || videoResolutionOptions.length <= 1}
                          className="flex-1 h-8 rounded-md text-xs font-semibold cursor-pointer disabled:opacity-50"
                          style={{
                            backgroundColor: resolution === item ? theme.colors.accentPrimary : "transparent",
                            color: resolution === item ? "#fff" : "var(--color-text-secondary)",
                          }}
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="rounded-md border p-3 space-y-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card }}>
                  <div className="flex items-center gap-2 text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>
                    <Lightbulb size={14} style={{ color: theme.colors.accentPrimary }} />
                    打光预设
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-1.5">
                    <button
                      type="button"
                      onClick={() => updateDraft({ lighting: "" })}
                      disabled={videoBusy}
                      className="h-8 rounded-md border px-2 text-xs font-semibold cursor-pointer disabled:opacity-50"
                      style={{
                        borderColor: !selectedLighting ? theme.colors.accentPrimary : "var(--color-border-primary)",
                        backgroundColor: !selectedLighting ? "var(--color-accent-light)" : SURFACE.soft,
                        color: !selectedLighting ? theme.colors.accentPrimary : "var(--color-text-secondary)",
                      }}
                    >
                      无打光
                    </button>
                    {LIGHTING_PRESETS.map((preset) => {
                      const active = selectedLighting === preset.id;
                      return (
                        <button
                          key={preset.id}
                          type="button"
                          onClick={() => updateDraft({ lighting: active ? "" : preset.id })}
                          disabled={videoBusy}
                          title={preset.prompt}
                          className="h-8 rounded-md border px-2 text-xs font-semibold truncate cursor-pointer disabled:opacity-50"
                          style={{
                            borderColor: active ? preset.color : "var(--color-border-primary)",
                            backgroundColor: active ? preset.colorSoft : SURFACE.soft,
                            color: active ? preset.color : "var(--color-text-secondary)",
                          }}
                        >
                          {preset.name}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="rounded-md border p-3 space-y-3" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card }}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="text-sm font-bold inline-flex items-center gap-2" style={{ color: "var(--color-text-primary)" }}>
                        <Images size={14} style={{ color: theme.colors.accentPrimary }} />
                        本张视频输入包
                      </div>
                      <div className="text-xs mt-1" style={{ color: "var(--color-text-muted)" }}>
                        已按本张故事板识别到的人物、场景、道具预填；你仍可删除、补图或调整 @图 顺序。
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={restoreDefaultPackage}
                      disabled={videoBusy}
                      className="h-8 px-2 rounded-md border text-xs font-semibold cursor-pointer disabled:opacity-50"
                      style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft, color: "var(--color-text-secondary)" }}
                    >
                      恢复本张默认
                    </button>
                  </div>

                  <ModelArkReferenceLibraryPanel projectId={projectId} provider={videoProvider} references={videoRefs} />
                  {videoRefs.length ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2">
                      {videoRefs.map((ref, refIndex) => {
                        const guaMeta = getPackageItemGuaMeta(ref, assetMetaMap, videoProvider);
                        const isActiveAsset = guaMeta.status === "Active" && !!guaMeta.assetId;
                        const isProcessingAsset = guaMeta.status === "Processing";
                        const isUploadingAsset = uploadingAssetUrls?.has?.(normalizeImageUrl(ref.url));
                        return (
                        <div
                          key={`${ref.url}-${refIndex}`}
                          className="rounded-md border overflow-hidden relative"
                          style={{ borderColor: isActiveAsset ? "var(--color-success)" : "var(--color-border-primary)", borderWidth: isActiveAsset ? 2 : 1, backgroundColor: SURFACE.soft }}
                        >
                          <HoverImagePreview src={getSeedance25ReferenceMediaType(ref) === "image" ? ref.url : ""} title={ref.name} className="block">
                            <button
                              type="button"
                              onClick={() => getSeedance25ReferenceMediaType(ref) === "image" && onPreview?.({ url: ref.url, title: ref.name })}
                              className="aspect-video w-full relative block cursor-pointer"
                              title={ref.name}
                            >
                              <ReferenceMediaThumbnail item={ref} />
                            <span className="absolute left-1.5 top-1.5 rounded px-1.5 py-0.5 text-[10px] font-bold" style={{ backgroundColor: "var(--overlay-medium)", color: "#fff" }}>
                              @图{refIndex + 1}
                            </span>
                            {isActiveAsset && (
                              <span className="absolute right-1.5 top-1.5 w-5 h-5 rounded-md inline-flex items-center justify-center" style={{ backgroundColor: "color-mix(in srgb, var(--color-success) 90%, transparent)", color: "#fff" }} title={guaMeta.assetId || "已入库"}>
                                <Check size={12} strokeWidth={3} />
                              </span>
                            )}
                            </button>
                          </HoverImagePreview>
                          <div className="p-2 flex items-center gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="text-xs font-bold truncate" style={{ color: "var(--color-text-primary)" }}>{ref.name}</div>
                              <div className="text-[11px] truncate" style={{ color: isActiveAsset ? "var(--color-success)" : "var(--color-text-muted)" }}>
                                {showGuaAssetUpload && isActiveAsset ? `已入库 ${videoProviderLabel}` : showGuaAssetUpload && isProcessingAsset ? "入库处理中" : (guaMeta.status || ref.type || ref.role)}
                              </div>
                            </div>
                            {showGuaAssetUpload && !isActiveAsset && (
                              <button
                                type="button"
                                onClick={() => onUploadPackageItem?.(ref)}
                                disabled={videoBusy || isProcessingAsset || isUploadingAsset}
                                className="h-7 rounded-md border px-2 inline-flex items-center gap-1 text-[11px] font-semibold cursor-pointer disabled:opacity-50"
                                style={{
                                  borderColor: "var(--color-border-primary)",
                                  backgroundColor: isProcessingAsset ? "var(--color-bg-overlay)" : "var(--color-accent-light)",
                                  color: isProcessingAsset ? "var(--color-text-muted)" : theme.colors.accentPrimary,
                                }}
                                title={isProcessingAsset ? (guaMeta.assetId || "入库处理中") : `加入${assetLibraryLabel(videoProvider)}`}
                              >
                                {isUploadingAsset || isProcessingAsset ? <Loader2 size={12} className="animate-spin" /> : <PackagePlus size={12} />}
                                {isUploadingAsset ? "提交中" : isProcessingAsset ? "处理中" : "入库"}
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => removePackageItem(ref.url)}
                              disabled={videoBusy}
                              className="w-7 h-7 rounded-md border inline-flex items-center justify-center cursor-pointer disabled:opacity-50"
                              style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card, color: "var(--color-text-muted)" }}
                              title="从本次视频输入包移除"
                            >
                              <X size={13} />
                            </button>
                          </div>
                        </div>
                      );})}
                    </div>
                  ) : (
                    <div className="rounded-md border border-dashed p-4 text-center text-sm" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-muted)", backgroundColor: SURFACE.soft }}>
                      当前没有图片输入。至少建议保留本张故事板，否则视频模型只能按纯文本生成。
                    </div>
                  )}

                  {candidatePackageItems.some((item) => !videoRefs.some((ref) => ref.url === item.url)) && (
                    <div className="space-y-2">
                      <div className="text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>可加入当前故事板</div>
                      <div className="flex flex-wrap gap-2">
                        {candidatePackageItems
                          .filter((item) => !videoRefs.some((ref) => ref.url === item.url))
                          .map((item) => (
                            <button
                              key={item.url}
                              type="button"
                              onClick={() => addPackageItem(item)}
                              disabled={videoBusy || videoRefs.length >= videoReferenceLimit}
                              className="h-8 rounded-md border px-2 inline-flex items-center gap-2 text-xs font-semibold cursor-pointer disabled:opacity-50"
                              style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft, color: "var(--color-text-secondary)" }}
                            >
                              <HoverImagePreview src={item.url} title={item.name} className="block shrink-0">
                                <img src={item.url} alt={item.name} className="w-5 h-5 rounded object-cover" />
                              </HoverImagePreview>
                              {item.name}
                            </button>
                          ))}
                      </div>
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <div className="text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>前15秒摘要</div>
                    <AutoGrowTextarea
                      value={previousSummary}
                      onChange={(event) => updateDraft({ previousSummary: event.target.value })}
                      disabled={videoBusy}
                      minRows={2}
                      maxHeight={180}
                      className="w-full rounded-md border px-3 py-2 text-xs leading-5 outline-none disabled:opacity-60"
                      style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.input, color: "var(--color-text-primary)" }}
                      placeholder="用于告诉视频模型上一段发生了什么；可删掉，也可自己改写。"
                    />
                  </div>
                </div>

                <details open className="rounded-lg border overflow-hidden" style={{ borderColor: theme.colors.accentPrimary, backgroundColor: SURFACE.card }}>
                  <summary className="px-3 py-2.5 cursor-pointer text-sm font-semibold select-none" style={{ color: theme.colors.accentPrimary, backgroundColor: "var(--color-accent-light)" }}>
                    <span className="inline-flex items-center gap-2">
                      <FileText size={14} />
                      视频提示词
                      <span className="rounded px-1.5 py-0.5 text-[10px] font-bold" style={{ backgroundColor: SURFACE.card, color: "var(--color-text-muted)" }}>可编辑</span>
                    </span>
                  </summary>
                  <div className="px-3 pb-3 space-y-2">
                    <div className="relative">
                      <div
                        ref={promptEditorRef}
                        contentEditable={!videoBusy}
                        suppressContentEditableWarning
                        onInput={handlePromptEditorInput}
                        onKeyDown={handlePromptKeyDown}
                        onPaste={(event) => {
                          const html = event.clipboardData.getData("text/html");
                          if (html && html.includes("data-mention")) return;
                          event.preventDefault();
                          document.execCommand("insertText", false, event.clipboardData.getData("text/plain"));
                        }}
                        className="w-full rounded-lg border px-3 py-2.5 text-[13px] leading-6 outline-none min-h-[180px] max-h-[520px] overflow-y-auto whitespace-pre-wrap break-words custom-scrollbar"
                        style={{
                          borderColor: "var(--color-border-primary)",
                          backgroundColor: videoBusy ? SURFACE.soft : SURFACE.input,
                          color: "var(--color-text-primary)",
                          opacity: videoBusy ? 0.68 : 1,
                        }}
                      />
                      {mentionPopover && filteredMentionItems.length > 0 && (
                        <div
                          className="absolute left-2 top-full mt-1 z-50 w-72 max-h-72 overflow-y-auto rounded-md border p-1 shadow-xl custom-scrollbar animate-in fade-in zoom-in-95 duration-150"
                          style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}
                        >
                          {filteredMentionItems.map((item, itemIndex) => (
                            <button
                              key={`${item.url}-${itemIndex}`}
                              type="button"
                              onMouseDown={(event) => {
                                event.preventDefault();
                                handleMentionSelect(item);
                              }}
                              className="w-full rounded-md px-2 py-2 flex items-center gap-2 text-left cursor-pointer"
                              style={{ color: "var(--color-text-secondary)" }}
                            >
                              <HoverImagePreview src={item.url} title={item.name} className="block shrink-0">
                                <img src={item.url} alt={item.name} className="w-10 h-8 rounded object-cover shrink-0" />
                              </HoverImagePreview>
                              <span className="min-w-0">
                                <span className="block text-xs font-bold truncate" style={{ color: "var(--color-text-primary)" }}>{item.name}</span>
                                <span className="block text-[11px] truncate" style={{ color: "var(--color-text-muted)" }}>@ 插入到提示词，并加入本张输入包</span>
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        updateDraft({ prompt: "" });
                        if (promptEditorRef.current) {
                          renderPromptToEditor(promptEditorRef.current, hydratePromptReferenceChips(defaultVideoPrompt, videoRefs), theme.colors.accentPrimary, true);
                        }
                      }}
                      disabled={videoBusy}
                      className="h-8 px-2 rounded-md border text-xs font-semibold cursor-pointer disabled:opacity-50"
                      style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft, color: "var(--color-text-muted)" }}
                    >
                      还原默认提示词
                    </button>
                  </div>
                </details>

              </div>
            ) : (
              <div className="p-3 flex flex-col gap-2" style={{ color: "var(--color-text-muted)" }}>
                <div className="text-sm font-semibold" style={{ color: "var(--color-text-secondary)" }}>本张视频配置已收起</div>
                <div className="text-xs leading-5">展开后可以单独选择当前故事板要用的人物、场景、道具参考图，也能修改提示词和比例。</div>
              </div>
            )}
            </div>
          </section>

          <section className="rounded-md border overflow-hidden" style={{
            borderColor: videoFailed ? "var(--color-error)" : videoSuccess ? "var(--color-success)" : videoStopped ? "var(--color-warning)" : "var(--color-border-primary)",
            backgroundColor: SURFACE.panel,
          }}>
            <div className="px-3 py-2 border-b flex items-center justify-between gap-3" style={{ borderColor: "var(--color-border-primary)" }}>
              <div className="flex items-center gap-2 text-sm font-bold" style={{
                color: videoFailed ? "var(--color-error)" : videoSuccess ? "var(--color-success)" : videoStopped ? "var(--color-warning)" : "var(--color-text-primary)",
              }}>
                {videoFailed ? <AlertTriangle size={15} /> : videoSuccess ? <CheckCircle2 size={15} /> : videoStopped ? <CircleStop size={15} /> : videoBusy ? <Loader2 size={15} className="animate-spin" /> : <Film size={15} />}
                视频结果
              </div>
              {videoTask?.taskId && (
                <span className="text-xs truncate max-w-[220px]" style={{ color: "var(--color-text-muted)" }}>任务 {videoTask.taskId}</span>
              )}
            </div>
            <div className="p-3">
              {videoSuccess ? (
                <div className="ui-generation-result-enter space-y-2">
                  <video src={videoUrl} controls className="w-full aspect-video rounded-md border object-contain max-h-[240px]" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "#000" }} />
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                      {videoTask?.originalVideoUrl ? "可对方舟原始视频继续去字幕" : "当前没有可用的方舟原始视频链接"}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => onDownloadVideoHistory?.(
                          board,
                          videoHistoryItems.find((item) => firstText(item.videoUrl, item.url) === videoUrl) || { videoUrl, url: videoUrl }
                        )}
                        disabled={!videoUrl}
                        className="h-8 px-2 rounded-md border text-xs font-bold inline-flex items-center gap-1.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          borderColor: "var(--color-border-primary)",
                          backgroundColor: SURFACE.soft,
                          color: "var(--color-text-primary)",
                        }}
                      >
                        <Download size={13} />
                        下载
                      </button>
                      <button
                        type="button"
                        onClick={() => onEraseSubtitle?.(board)}
                        disabled={subtitleBusy || !videoTask?.originalVideoUrl}
                        className="h-8 px-2 rounded-md border text-xs font-bold inline-flex items-center gap-1.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          borderColor: "var(--color-error)",
                          backgroundColor: subtitleBusy ? SURFACE.soft : "color-mix(in srgb, var(--color-error) 10%, transparent)",
                          color: "var(--color-error)",
                        }}
                      >
                        {subtitleBusy ? <Loader2 size={13} className="animate-spin" /> : <RemoveFormatting size={13} />}
                        {subtitleBusy ? "去字幕中" : "去字幕"}
                      </button>
                    </div>
                  </div>
                </div>
              ) : videoFailed ? (
                <div className="min-h-[150px] rounded-md border border-dashed flex flex-col items-center justify-center gap-2 px-4 text-center" style={{ borderColor: "var(--color-error)", color: "var(--color-error)", backgroundColor: "rgba(220,38,38,0.08)" }}>
                  <AlertTriangle size={22} />
                  <div className="text-sm font-bold">视频生成失败</div>
                  <div className="text-xs leading-5 whitespace-pre-wrap break-words max-w-full">{videoTask?.error || "服务商返回失败，可以调整参数后重新提交。"}</div>
                </div>
              ) : videoStopped ? (
                <div className="min-h-[150px] rounded-md border border-dashed flex flex-col items-center justify-center gap-2 px-4 text-center" style={{ borderColor: "var(--color-warning)", color: "var(--color-warning)", backgroundColor: "rgba(245,158,11,0.08)" }}>
                  <CircleStop size={22} />
                  <div className="text-sm font-bold">已停止等待</div>
                  <div className="text-xs leading-5 whitespace-pre-wrap break-words max-w-full">{videoTask?.error || "前端已停止轮询；需要时可以重新提交本张视频。"}</div>
                </div>
              ) : videoBusy ? (
                <div className="min-h-[150px] rounded-md border p-4 flex flex-col justify-center gap-3" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft }}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>
                      <Loader2 size={16} className="animate-spin" />
                      {videoProviderLabel} 正在生成
                    </div>
                    <button
                      type="button"
                      onClick={() => onStopVideo?.(board)}
                      className="h-8 px-2 rounded-md border text-xs font-bold inline-flex items-center gap-1.5 cursor-pointer shrink-0"
                      style={{ borderColor: "var(--color-warning)", backgroundColor: "rgba(245,158,11,0.12)", color: "var(--color-warning)" }}
                    >
                      <CircleStop size={13} />
                      停止
                    </button>
                  </div>
                  <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: "var(--color-border-primary)" }}>
                    <div
                      className="h-full transition-all"
                      style={{ width: `${Math.max(5, Math.min(100, Number(videoTask?.progress) || 8))}%`, backgroundColor: theme.colors.accentPrimary }}
                    />
                  </div>
                  <div className="text-xs" style={{ color: "var(--color-text-muted)" }}>生成完成后会在这里显示视频，不会挤压故事板缩略图区域。</div>
                </div>
              ) : (
                <div className="min-h-[150px] rounded-md border border-dashed flex flex-col items-center justify-center gap-2 px-4 text-center" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-muted)", backgroundColor: SURFACE.soft }}>
                  <Film size={24} />
                  <div className="text-sm font-bold" style={{ color: "var(--color-text-secondary)" }}>尚未生成视频</div>
                  <div className="text-xs leading-5">确认本张输入包、提示词和参数后，点击“生视频”。</div>
                </div>
              )}
              <div className="grid grid-cols-3 gap-1.5 mt-3">
                {[
                  ["时长", videoTask?.duration || `${duration}s`],
                  ["画幅", videoTask?.ratio || ratio],
                  ["参考", `${videoTask?.referenceCount || videoRefs.length}张`],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-md border px-2 py-1.5" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft }}>
                    <div className="text-[10px]" style={{ color: "var(--color-text-muted)" }}>{label}</div>
                    <div className="text-xs font-bold truncate" style={{ color: "var(--color-text-secondary)" }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </div>

      {SHOW_LEGACY_STORYBOARD_LAYOUT && (
      <div className="grid grid-cols-1 2xl:grid-cols-[64px_260px_minmax(0,1fr)_360px]">
        <div className="hidden 2xl:flex flex-col items-center gap-2 py-3 border-r" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
          <div
            className="w-9 h-9 rounded-md border flex items-center justify-center text-xs font-black"
            style={{
              borderColor: isFailed ? "var(--color-error)" : isSuccess ? "var(--color-success)" : "var(--color-border-primary)",
              backgroundColor: isSuccess ? "color-mix(in srgb, var(--color-success) 10%, transparent)" : isFailed ? "color-mix(in srgb, var(--color-error) 10%, transparent)" : SURFACE.soft,
              color: isFailed ? "var(--color-error)" : isSuccess ? "var(--color-success)" : "var(--color-text-muted)",
            }}
          >
            {String(board.index || 0).padStart(2, "0")}
          </div>
          <div className="w-px flex-1 min-h-12" style={{ backgroundColor: "var(--color-border-primary)" }} />
          <div className="text-[10px] font-mono text-center leading-4 px-1" style={{ color: "var(--color-text-muted)" }}>
            {board.timeRange || `${(Number(board.index || 1) - 1) * BOARD_SECONDS}s`}
          </div>
        </div>

        <div className="p-3 space-y-2 border-b 2xl:border-b-0 2xl:border-r" style={{ borderColor: "var(--color-border-primary)" }}>
          <div className="aspect-video rounded-md border overflow-hidden relative" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft }}>
            {isSuccess && boardImageUrl ? (
              <HoverImagePreview src={boardImageUrl} title={board.title || `第${board.index}张故事板`} className="ui-generation-result-enter block w-full h-full">
                <img src={boardImageUrl} alt={board.title || `故事板${board.index}`} className="w-full h-full object-contain" />
              </HoverImagePreview>
            ) : isFailed ? (
              <div className="w-full h-full flex flex-col items-center justify-center gap-2 px-4 text-center" style={{ color: "var(--color-error)" }}>
                <AlertTriangle size={24} />
                <div className="text-sm font-bold">本张生成失败</div>
                <div className="text-xs leading-5 line-clamp-3">{board.error || "可以重新发送要求，失败项会保留占位。"}</div>
              </div>
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-2" style={{ color: "var(--color-text-muted)" }}>
                <Loader2 size={24} className="animate-spin" />
                <div className="text-sm font-semibold">排队生成中</div>
              </div>
            )}
            <span className="absolute top-2 left-2 rounded-md px-2 py-1 text-xs font-bold" style={{ backgroundColor: "var(--overlay-medium)", color: "#fff" }}>
              #{board.index} · {board.timeRange}
            </span>
            {regenerating && (
              <div className="absolute inset-0 flex items-center justify-center gap-2 text-sm font-medium animate-in fade-in duration-150" style={{ backgroundColor: "var(--overlay-medium)", color: "#fff" }}>
                <Loader2 size={16} className="animate-spin" />
                重新生成中
              </div>
            )}
            {isSuccess && boardImageUrl && (
              <button
                type="button"
                onClick={() => onPreview?.({ url: boardImageUrl, title: board.title || `第${board.index}张故事板`, boardIndex: Number(board.index) })}
                className="absolute right-2 bottom-2 h-9 px-3 rounded-md border text-sm font-semibold inline-flex items-center gap-2 cursor-pointer"
                style={{ borderColor: "color-mix(in srgb, var(--color-accent-contrast) 35%, transparent)", backgroundColor: "var(--overlay-medium)", color: "#fff" }}
              >
                <ZoomIn size={15} />
                放大
              </button>
            )}
          </div>

          {videoRefs.length > 0 && (
            <div className="rounded-md border p-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft }}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-[11px] font-bold inline-flex items-center gap-1.5" style={{ color: "var(--color-text-secondary)" }}>
                  <Images size={12} style={{ color: theme.colors.accentPrimary }} />
                  输入包
                </span>
                <span className="text-[11px] font-mono" style={{ color: "var(--color-text-muted)" }}>{videoRefs.length} 张</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {videoRefs.slice(0, 6).map((ref, refIndex) => (
                  <HoverImagePreview key={`${ref.url}-${refIndex}`} src={getSeedance25ReferenceMediaType(ref) === "image" ? ref.url : ""} title={ref.name} className="block">
                    <button
                      type="button"
                      onClick={() => getSeedance25ReferenceMediaType(ref) === "image" && onPreview?.({ url: ref.url, title: ref.name })}
                      className="w-9 h-9 rounded-md border overflow-hidden relative cursor-pointer"
                      style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.input }}
                      title={ref.name}
                    >
                      <ReferenceMediaThumbnail item={ref} />
                      <span className="absolute left-0.5 top-0.5 rounded px-1 text-[9px] font-bold" style={{ backgroundColor: "var(--overlay-medium)", color: "#fff" }}>
                        {refIndex + 1}
                      </span>
                    </button>
                  </HoverImagePreview>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="min-w-0 space-y-3 p-3 border-b 2xl:border-b-0 2xl:border-r" style={{ borderColor: "var(--color-border-primary)" }}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-base font-bold truncate" style={{ color: "var(--color-text-primary)" }}>
                {board.title || `第${board.index}张故事板`}
              </div>
                <div className="text-xs mt-1" style={{ color: "var(--color-text-muted)" }}>
                  {duration} 秒视频段落
              </div>
            </div>
            <span
              className="shrink-0 rounded-md border px-2 py-1 text-xs font-bold inline-flex items-center gap-1"
              style={{
                borderColor: isFailed ? "var(--color-error)" : isSuccess ? "var(--color-success)" : "var(--color-border-primary)",
                color: isFailed ? "var(--color-error)" : isSuccess ? "var(--color-success)" : "var(--color-text-muted)",
                backgroundColor: isFailed ? "color-mix(in srgb, var(--color-error) 10%, transparent)" : isSuccess ? "color-mix(in srgb, var(--color-success) 10%, transparent)" : SURFACE.soft,
              }}
            >
              {isFailed ? <AlertTriangle size={13} /> : isSuccess ? <CheckCircle2 size={13} /> : <Loader2 size={13} className="animate-spin" />}
              {isFailed ? "失败" : isSuccess ? "故事板已生成" : "故事板生成中"}
            </span>
          </div>

          <div className="rounded-md border p-3 text-sm leading-6" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft, color: "var(--color-text-secondary)" }}>
            {board.segmentText || "本张对应一个15秒段落，生成完成后会显示当前段落原剧本文本。"}
          </div>

          {renderImagePromptPanel(true)}

          <div className="space-y-3">
            <section className="rounded-md border overflow-hidden" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}>
              <div className="px-3 py-2 flex flex-wrap items-center justify-between gap-2" style={{ borderColor: "var(--color-border-primary)" }}>
                <button
                  type="button"
                  onClick={() => onToggleVideoOptions?.(board.index)}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-md cursor-pointer transition-colors"
                  style={{
                    backgroundColor: videoOptionsOpen ? theme.colors.accentPrimary : `${theme.colors.accentPrimary}15`,
                    border: `1.5px solid ${videoOptionsOpen ? theme.colors.accentPrimary : `${theme.colors.accentPrimary}40`}`,
                    color: videoOptionsOpen ? "var(--color-accent-contrast)" : theme.colors.accentPrimary,
                  }}
                >
                  <Film size={12} />
                  <span className="text-xs font-bold">{videoProviderLabel}</span>
                  <span
                    className="text-[10px] px-1 py-px rounded leading-none font-bold"
                    style={{
                      backgroundColor: videoOptionsOpen ? "color-mix(in srgb, var(--color-accent-contrast) 24%, transparent)" : `${theme.colors.accentPrimary}20`,
                      color: videoOptionsOpen ? "var(--color-accent-contrast)" : theme.colors.accentPrimary,
                    }}
                  >
                    参考生成
                  </span>
                  <Settings2 size={11} style={{ opacity: 0.72 }} />
                  {videoOptionsOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                </button>

                <div className="flex flex-wrap items-center justify-end gap-1.5">
                  <span className="h-7 px-2 rounded-md border text-xs inline-flex items-center" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft, color: "var(--color-text-secondary)" }}>{duration}s</span>
                  <span className="h-7 px-2 rounded-md border text-xs inline-flex items-center" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft, color: "var(--color-text-secondary)" }}>{ratio === "adaptive" ? "自适应" : ratio}</span>
                  <span className="h-7 px-2 rounded-md border text-xs inline-flex items-center" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft, color: "var(--color-text-secondary)" }}>{resolution}</span>
                  <span className="h-7 px-2 rounded-md border text-xs inline-flex items-center" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft, color: "var(--color-text-secondary)" }}>{!hasAudioControl ? "声音平台自动" : generateAudio ? "有声" : "静音"}</span>
                  <span className="h-7 px-2 rounded-md border text-xs inline-flex items-center" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft, color: "var(--color-text-secondary)" }}>输入包 {videoRefs.length} 张</span>
                  <button
                    type="button"
                    onClick={() => onRegenerate?.(board)}
                    disabled={disabled || regenerating || videoBusy || !board.index || status === "pending"}
                    className="h-7 px-2 rounded-md border text-xs font-semibold inline-flex items-center gap-1 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ borderColor: theme.colors.accentPrimary, backgroundColor: "var(--color-accent-light)", color: theme.colors.accentPrimary }}
                  >
                    {regenerating ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                    重生图
                  </button>
                  {showGuaAssetUpload && (
                    <button
                      type="button"
                      onClick={() => onUploadBoardAsset?.(board)}
                      disabled={!boardImageUrl || boardAssetUploading || boardAssetProcessing || boardAssetActive}
                      className="h-7 px-2 rounded-md border text-xs font-semibold inline-flex items-center gap-1 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                      style={{
                        borderColor: boardAssetActive ? "var(--color-success)" : theme.colors.accentPrimary,
                        backgroundColor: boardAssetActive ? "rgba(34,197,94,0.12)" : "var(--color-accent-light)",
                        color: boardAssetActive ? "var(--color-success)" : theme.colors.accentPrimary,
                      }}
                      title={boardEffectiveAssetMeta.assetId || `把本张故事板加入资源中心并提交${assetLibraryLabel(videoProvider)}`}
                    >
                      {boardAssetUploading || boardAssetProcessing ? <Loader2 size={12} className="animate-spin" /> : boardAssetActive ? <Check size={12} /> : <PackagePlus size={12} />}
                      {boardAssetUploading ? "入库中" : boardAssetProcessing ? "处理中" : boardAssetActive ? "已入库" : "入库"}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onGenerateVideo?.(board)}
                    disabled={disabled || regenerating || videoBusy || !isSuccess || !boardImageUrl}
                    className="h-7 px-2 rounded-md border text-xs font-bold inline-flex items-center gap-1 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ borderColor: theme.colors.accentPrimary, backgroundColor: theme.colors.accentPrimary, color: "var(--color-accent-contrast)" }}
                  >
                    {videoBusy ? <Loader2 size={12} className="animate-spin" /> : <Film size={12} />}
                    {videoSuccess ? "重生视频" : "生视频"}
                  </button>
                </div>
              </div>

              {videoOptionsOpen && (
                <div className="border-t p-3 space-y-3 animate-in fade-in slide-in-from-top-1 duration-200" style={{ borderColor: "var(--color-border-primary)" }}>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                    <div className="rounded-md border p-3 space-y-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft }}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>时长</span>
                        <span className="text-xs font-mono px-2 py-0.5 rounded" style={{ backgroundColor: SURFACE.card, color: theme.colors.accentPrimary }}>{duration}s</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>{isSeedance25 ? seedance25Capabilities.minDuration : 4}</span>
                        <input
                          type="range"
                          min={isSeedance25 ? seedance25Capabilities.minDuration : 4}
                          max={isSeedance25 ? seedance25Capabilities.maxDuration : 15}
                          step={1}
                          value={duration}
                          onChange={(event) => updateDraft({
                            duration: isSeedance25
                              ? normalizeSeedance25Duration(videoProvider, event.target.value)
                              : normalizeVideoDuration(event.target.value),
                          })}
                          disabled={videoBusy}
                          className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer disabled:opacity-50"
                          style={{ accentColor: theme.colors.accentPrimary }}
                        />
                        <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>{isSeedance25 ? seedance25Capabilities.maxDuration : 15}</span>
                      </div>
                    </div>

                    <div className="rounded-md border p-3 space-y-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft }}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>有声视频</span>
                        {!hasAudioControl ? (
                          <span className="h-8 px-3 rounded-md border text-xs font-bold inline-flex items-center gap-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card, color: "var(--color-text-muted)" }}>
                            <Volume2 size={13} /> 平台自动
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => updateDraft({ generateAudio: !generateAudio })}
                            disabled={videoBusy}
                            className="h-8 px-3 rounded-md border text-xs font-bold inline-flex items-center gap-2 cursor-pointer disabled:opacity-50"
                            style={{
                              borderColor: generateAudio ? theme.colors.accentPrimary : "var(--color-border-primary)",
                              backgroundColor: generateAudio ? "var(--color-accent-light)" : SURFACE.card,
                              color: generateAudio ? theme.colors.accentPrimary : "var(--color-text-muted)",
                            }}
                          >
                            <Volume2 size={13} />
                            {generateAudio ? "开启" : "关闭"}
                          </button>
                        )}
                      </div>
                      <div className="text-xs" style={{ color: "var(--color-text-muted)" }}>{!hasAudioControl ? `${videoProviderLabel} 当前协议不提供有声开关。` : `当前使用 ${videoProviderLabel}，有声开关会随任务提交。`}</div>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <div className="text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>画面比例</div>
                    <div className="grid grid-cols-7 gap-1 p-1 rounded-md" style={{ backgroundColor: SURFACE.soft }}>
                      {(isSeedance25 ? seedance25Capabilities.ratios : GUA_VIDEO_RATIOS).map((item) => (
                        <button
                          key={item}
                          type="button"
                          onClick={() => updateDraft({ ratio: item })}
                          disabled={videoBusy}
                          className="h-8 rounded-md text-xs font-semibold cursor-pointer disabled:opacity-50"
                          style={{
                            backgroundColor: ratio === item ? theme.colors.accentPrimary : "transparent",
                            color: ratio === item ? "#fff" : "var(--color-text-secondary)",
                          }}
                        >
                          {item === "adaptive" ? "自适应" : item}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <div className="text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>分辨率</div>
                    <div className="flex gap-1 p-1 rounded-md w-full max-w-[260px]" style={{ backgroundColor: SURFACE.soft }}>
                      {videoResolutionOptions.map((item) => (
                        <button
                          key={item}
                          type="button"
                          onClick={() => updateDraft({ resolution: item })}
                          disabled={videoBusy || videoResolutionOptions.length <= 1}
                          className="flex-1 h-8 rounded-md text-xs font-semibold cursor-pointer disabled:opacity-50"
                          style={{
                            backgroundColor: resolution === item ? theme.colors.accentPrimary : "transparent",
                            color: resolution === item ? "#fff" : "var(--color-text-secondary)",
                          }}
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-md border p-3 space-y-3" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card }}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div className="text-sm font-bold inline-flex items-center gap-2" style={{ color: "var(--color-text-primary)" }}>
                          <Images size={14} style={{ color: theme.colors.accentPrimary }} />
                          视频输入包
                        </div>
                        <div className="text-xs mt-1" style={{ color: "var(--color-text-muted)" }}>
                          顺序会影响提示词里的 @图编号；可删除、补回，也可在提示词里输入 @ 插入引用。
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={restoreDefaultPackage}
                        disabled={videoBusy}
                        className="h-8 px-2 rounded-md border text-xs font-semibold cursor-pointer disabled:opacity-50"
                        style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft, color: "var(--color-text-secondary)" }}
                      >
                        恢复默认输入包
                      </button>
                    </div>

                    <ModelArkReferenceLibraryPanel projectId={projectId} provider={videoProvider} references={videoRefs} />
                    {videoRefs.length ? (
                      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2">
                        {videoRefs.map((ref, refIndex) => (
                          <div
                            key={`${ref.url}-${refIndex}`}
                            className="rounded-md border overflow-hidden"
                            style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft }}
                          >
                            <HoverImagePreview src={getSeedance25ReferenceMediaType(ref) === "image" ? ref.url : ""} title={ref.name} className="block">
                              <button
                                type="button"
                                onClick={() => getSeedance25ReferenceMediaType(ref) === "image" && onPreview?.({ url: ref.url, title: ref.name })}
                                className="aspect-video w-full relative block cursor-pointer"
                                title={ref.name}
                              >
                                <ReferenceMediaThumbnail item={ref} />
                              <span className="absolute left-1.5 top-1.5 rounded px-1.5 py-0.5 text-[10px] font-bold" style={{ backgroundColor: "var(--overlay-medium)", color: "#fff" }}>
                                @图{refIndex + 1}
                              </span>
                              </button>
                            </HoverImagePreview>
                            <div className="p-2 flex items-center gap-2">
                              <div className="min-w-0 flex-1">
                                <div className="text-xs font-bold truncate" style={{ color: "var(--color-text-primary)" }}>{ref.name}</div>
                                <div className="text-[11px] truncate" style={{ color: "var(--color-text-muted)" }}>{ref.type || ref.role}</div>
                              </div>
                              <button
                                type="button"
                                onClick={() => removePackageItem(ref.url)}
                                disabled={videoBusy}
                                className="w-7 h-7 rounded-md border inline-flex items-center justify-center cursor-pointer disabled:opacity-50"
                                style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card, color: "var(--color-text-muted)" }}
                                title="从本次视频输入包移除"
                              >
                                <X size={13} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-md border border-dashed p-4 text-center text-sm" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-muted)", backgroundColor: SURFACE.soft }}>
                        当前没有图片输入。至少建议保留本张故事板，否则视频模型只能按纯文本生成。
                      </div>
                    )}

                    {defaultPackageItems.some((item) => !videoRefs.some((ref) => ref.url === item.url)) && (
                      <div className="space-y-2">
                        <div className="text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>可补回参考图</div>
                        <div className="flex flex-wrap gap-2">
                          {defaultPackageItems
                            .filter((item) => !videoRefs.some((ref) => ref.url === item.url))
                            .map((item) => (
                              <button
                                key={item.url}
                                type="button"
                                onClick={() => addPackageItem(item)}
                                disabled={videoBusy || videoRefs.length >= videoReferenceLimit}
                                className="h-8 rounded-md border px-2 inline-flex items-center gap-2 text-xs font-semibold cursor-pointer disabled:opacity-50"
                                style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft, color: "var(--color-text-secondary)" }}
                              >
                                <HoverImagePreview src={item.url} title={item.name} className="block shrink-0">
                                  <img src={item.url} alt={item.name} className="w-5 h-5 rounded object-cover" />
                                </HoverImagePreview>
                                {item.name}
                              </button>
                            ))}
                        </div>
                      </div>
                    )}

                    <div className="space-y-1.5">
                      <div className="text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>前15秒摘要</div>
                      <AutoGrowTextarea
                        value={previousSummary}
                        onChange={(event) => updateDraft({ previousSummary: event.target.value })}
                        disabled={videoBusy}
                        minRows={2}
                        maxHeight={180}
                        className="w-full rounded-md border px-3 py-2 text-xs leading-5 outline-none disabled:opacity-60"
                        style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.input, color: "var(--color-text-primary)" }}
                        placeholder="用于告诉视频模型上一段发生了什么；可删掉，也可自己改写。"
                      />
                    </div>
                  </div>

                  <details open className="rounded-lg border overflow-hidden" style={{ borderColor: theme.colors.accentPrimary, backgroundColor: SURFACE.card }}>
                    <summary className="px-3 py-2.5 cursor-pointer text-sm font-semibold select-none" style={{ color: theme.colors.accentPrimary, backgroundColor: "var(--color-accent-light)" }}>
                      <span className="inline-flex items-center gap-2">
                        <FileText size={14} />
                        视频提示词
                        <span className="rounded px-1.5 py-0.5 text-[10px] font-bold" style={{ backgroundColor: SURFACE.card, color: "var(--color-text-muted)" }}>可编辑</span>
                      </span>
                    </summary>
                    <div className="px-3 pb-3 space-y-2">
                      <div className="relative">
                        <div
                          ref={promptEditorRef}
                          contentEditable={!videoBusy}
                          suppressContentEditableWarning
                          onInput={handlePromptEditorInput}
                          onKeyDown={handlePromptKeyDown}
                          onPaste={(event) => {
                            const html = event.clipboardData.getData("text/html");
                            if (html && html.includes("data-mention")) return;
                            event.preventDefault();
                            document.execCommand("insertText", false, event.clipboardData.getData("text/plain"));
                          }}
                          className="w-full rounded-lg border px-3 py-2.5 text-[13px] leading-6 outline-none min-h-[180px] max-h-[520px] overflow-y-auto whitespace-pre-wrap break-words custom-scrollbar"
                          style={{
                            borderColor: "var(--color-border-primary)",
                            backgroundColor: videoBusy ? SURFACE.soft : SURFACE.input,
                            color: "var(--color-text-primary)",
                            opacity: videoBusy ? 0.68 : 1,
                          }}
                        />
                        {mentionPopover && filteredMentionItems.length > 0 && (
                          <div
                            className="absolute left-2 top-full mt-1 z-50 w-72 max-h-72 overflow-y-auto rounded-md border p-1 shadow-xl custom-scrollbar animate-in fade-in zoom-in-95 duration-150"
                            style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}
                          >
                            {filteredMentionItems.map((item, itemIndex) => (
                              <button
                                key={`${item.url}-${itemIndex}`}
                                type="button"
                                onMouseDown={(event) => {
                                  event.preventDefault();
                                  handleMentionSelect(item);
                                }}
                                className="w-full rounded-md px-2 py-2 flex items-center gap-2 text-left cursor-pointer"
                                style={{ color: "var(--color-text-secondary)" }}
                              >
                                <HoverImagePreview src={item.url} title={item.name} className="block shrink-0">
                                  <img src={item.url} alt={item.name} className="w-10 h-8 rounded object-cover shrink-0" />
                                </HoverImagePreview>
                                <span className="min-w-0">
                                  <span className="block text-xs font-bold truncate" style={{ color: "var(--color-text-primary)" }}>{item.name}</span>
                                  <span className="block text-[11px] truncate" style={{ color: "var(--color-text-muted)" }}>@ 插入到提示词，并加入输入包</span>
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          updateDraft({ prompt: "" });
                          if (promptEditorRef.current) {
                            renderPromptToEditor(promptEditorRef.current, hydratePromptReferenceChips(defaultVideoPrompt, videoRefs), theme.colors.accentPrimary, true);
                          }
                        }}
                        disabled={videoBusy}
                        className="h-8 px-2 rounded-md border text-xs font-semibold cursor-pointer disabled:opacity-50"
                        style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft, color: "var(--color-text-muted)" }}
                      >
                        还原默认提示词
                      </button>
                    </div>
                  </details>

                </div>
              )}
            </section>

          </div>
        </div>

        <div className="p-3 min-w-0">
          <section className="rounded-md border overflow-hidden" style={{
              borderColor: videoFailed ? "var(--color-error)" : videoSuccess ? "var(--color-success)" : "var(--color-border-primary)",
              backgroundColor: SURFACE.panel,
            }}>
              <div className="px-3 py-2 border-b flex items-center justify-between gap-3" style={{ borderColor: "var(--color-border-primary)" }}>
                <div className="flex items-center gap-2 text-sm font-bold" style={{
                  color: videoFailed ? "var(--color-error)" : videoSuccess ? "var(--color-success)" : "var(--color-text-primary)",
                }}>
                  {videoFailed ? <AlertTriangle size={15} /> : videoSuccess ? <CheckCircle2 size={15} /> : videoBusy ? <Loader2 size={15} className="animate-spin" /> : <Film size={15} />}
                  视频结果
                </div>
                {videoTask?.taskId && (
                  <span className="text-xs truncate max-w-[220px]" style={{ color: "var(--color-text-muted)" }}>任务 {videoTask.taskId}</span>
                )}
              </div>
              <div className="p-3">
                {videoSuccess ? (
                  <div className="ui-generation-result-enter space-y-2">
                    <video src={videoUrl} controls className="w-full max-w-[720px] mx-auto aspect-video rounded-md border object-contain max-h-[240px]" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "#000" }} />
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => onDownloadVideoHistory?.(
                          board,
                          videoHistoryItems.find((item) => firstText(item.videoUrl, item.url) === videoUrl) || { videoUrl, url: videoUrl }
                        )}
                        disabled={!videoUrl}
                        className="h-8 px-2 rounded-md border text-xs font-bold inline-flex items-center gap-1.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          borderColor: "var(--color-border-primary)",
                          backgroundColor: SURFACE.soft,
                          color: "var(--color-text-primary)",
                        }}
                      >
                        <Download size={13} />
                        下载
                      </button>
                    </div>
                  </div>
                ) : videoFailed ? (
                  <div className="min-h-[150px] max-w-[720px] mx-auto rounded-md border border-dashed flex flex-col items-center justify-center gap-2 px-4 text-center" style={{ borderColor: "var(--color-error)", color: "var(--color-error)", backgroundColor: "rgba(220,38,38,0.08)" }}>
                    <AlertTriangle size={22} />
                    <div className="text-sm font-bold">视频生成失败</div>
                    <div className="text-xs leading-5">{videoTask?.error || "服务商返回失败，可以调整参数后重新提交。"}</div>
                  </div>
                ) : videoBusy ? (
                  <div className="min-h-[150px] max-w-[720px] mx-auto rounded-md border p-4 flex flex-col justify-center gap-3" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft }}>
                    <div className="flex items-center gap-2 text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>
                      <Loader2 size={16} className="animate-spin" />
                      {videoProviderLabel} 正在生成
                    </div>
                    <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: "var(--color-border-primary)" }}>
                      <div
                        className="h-full transition-all"
                        style={{ width: `${Math.max(5, Math.min(100, Number(videoTask?.progress) || 8))}%`, backgroundColor: theme.colors.accentPrimary }}
                      />
                    </div>
                    <div className="text-xs" style={{ color: "var(--color-text-muted)" }}>生成完成后会在这里显示视频，不会挤压故事板缩略图区域。</div>
                  </div>
                ) : (
                  <div className="min-h-[150px] max-w-[720px] mx-auto rounded-md border border-dashed flex flex-col items-center justify-center gap-2 px-4 text-center" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-muted)", backgroundColor: SURFACE.soft }}>
                    <Film size={24} />
                    <div className="text-sm font-bold" style={{ color: "var(--color-text-secondary)" }}>尚未生成视频</div>
                    <div className="text-xs leading-5">确认视频输入包、提示词和参数后，点击“生视频”。</div>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-1.5 mt-3">
                  {[
                    ["时长", videoTask?.duration || `${duration}s`],
                    ["画幅", videoTask?.ratio || ratio],
                    ["参考", `${videoTask?.referenceCount || videoRefs.length}张`],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-md border px-2 py-1.5" style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.soft }}>
                      <div className="text-[10px]" style={{ color: "var(--color-text-muted)" }}>{label}</div>
                      <div className="text-xs font-bold truncate" style={{ color: "var(--color-text-secondary)" }}>{value}</div>
                    </div>
                  ))}
                </div>
              </div>
          </section>
        </div>
      </div>)}
      </article>
    </div>
  );
};

const areStoryboardBoardListItemsEqual = (prev, next) => (
  prev.projectId === next.projectId
  && prev.board === next.board
  && prev.boards === next.boards
  && prev.episode === next.episode
  && prev.selectedRefs === next.selectedRefs
  && prev.aspectRatio === next.aspectRatio
  && prev.directorRequest === next.directorRequest
  && prev.pendingDirectorAction === next.pendingDirectorAction
  && prev.directorBusy === next.directorBusy
  && prev.loading === next.loading
  && prev.hasGeneratedBoards === next.hasGeneratedBoards
  && prev.presentationMode === next.presentationMode
  && prev.boardImageHistory === next.boardImageHistory
  && prev.regenerating === next.regenerating
  && prev.disabled === next.disabled
  && prev.setDirectorRequest === next.setDirectorRequest
  && prev.onSendDirectorMessage === next.onSendDirectorMessage
  && prev.onOpenReferencePicker === next.onOpenReferencePicker
  && prev.onOpenVideoReferencePicker === next.onOpenVideoReferencePicker
  && prev.onRemoveSelectedRef === next.onRemoveSelectedRef
  && prev.onMatchRefsFromScript === next.onMatchRefsFromScript
  && prev.onClearSelectedRefs === next.onClearSelectedRefs
  && prev.onExecutePendingSingleBoard === next.onExecutePendingSingleBoard
  && prev.onExecutePendingAllBoards === next.onExecutePendingAllBoards
  && prev.onKeepPendingAsNote === next.onKeepPendingAsNote
  && prev.onCancelPendingAction === next.onCancelPendingAction
  && prev.onStopVideo === next.onStopVideo
  && prev.onSelectBoardImageHistory === next.onSelectBoardImageHistory
  && prev.onSelectVideoHistory === next.onSelectVideoHistory
  && prev.onDownloadVideoHistory === next.onDownloadVideoHistory
  && prev.onUploadBoardAsset === next.onUploadBoardAsset
  && prev.videoDraft === next.videoDraft
  && prev.videoOptionsOpen === next.videoOptionsOpen
  && prev.videoTask === next.videoTask
  && prev.videoGenerating === next.videoGenerating
  && prev.assetMetaMap === next.assetMetaMap
  && prev.uploadingAssetUrls === next.uploadingAssetUrls
  && prev.videoProvider === next.videoProvider
  && prev.videoProviderLabel === next.videoProviderLabel
  && prev.showGuaAssetUpload === next.showGuaAssetUpload
  && prev.focusMode === next.focusMode
  && prev.focusColumnPercents === next.focusColumnPercents
  && prev.onFocusColumnPercentsChange === next.onFocusColumnPercentsChange
  && prev.theme?.colors?.accentPrimary === next.theme?.colors?.accentPrimary
);

const StoryboardBoardListItem = React.memo(StoryboardBoardListItemBase, areStoryboardBoardListItemsEqual);

export default StoryboardBoardListItem;
